/**
 * Elite Batch 12 Part 4 — Workflow Templates 1108–1127
 * Weekly content planning, editorial calendars, offer fallback welcome, omnichannel
 * campaigns, onboarding drips, organic-to-paid video, paid media briefings,
 * pain-point agitation video, paywall conversion, persona-targeted video ads,
 * Pinterest ads/calendar/promoted/video, podcast nurture, points confirmation,
 * points-expiring reminder, post-holiday reactivation, post-purchase loyalty,
 * and predictive churn scoring.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function serpApiNode(
  id: string,
  label: string,
  y: number,
  query: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://serpapi.com/search.json?q=${encodeURIComponent(query)}&api_key={serpapi_key}`,
        headers: [],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function bufferNode(
  id: string,
  label: string,
  y: number,
  text: string,
  mediaUrl: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.bufferapp.com/1/updates/create.json",
        headers: [
          { key: "Authorization", value: "Bearer {buffer_token}" },
          { key: "Content-Type", value: "application/x-www-form-urlencoded" },
        ],
        body: [
          { key: "profile_ids[]", value: "{buffer_profile_id}" },
          { key: "text", value: text },
          { key: "media[link]", value: mediaUrl },
          { key: "shorten", value: "true" },
        ],
        variables: [
          { name: "success", customName: "buffer_success", type: "boolean" },
          {
            name: "updates[0].id",
            customName: "buffer_update_id",
            type: "string",
          },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function hubspotPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyFields: Array<{ key: string; value: string }>,
  outputs: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyFields,
        variables: outputs,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 12 PART 4 — TEMPLATES 1108–1127
// =============================================================================

export const ELITE_BATCH12_PART4: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1108. WEEKLY CONTENT PLANNER (8 nodes)
  // ===================================================================

  "Weekly Content Planner": {
    summary:
      "Automates weekly content planning by scanning trending topics via SerpAPI, identifying SEO opportunities with AI, generating a full content plan, populating a Google Sheets calendar, running a resource allocation check via code, distributing team briefs through Slack, and updating the tracking sheet with plan metadata.",
    nodes: [
      trigger("Schedule: Weekly content planning trigger", "schedule", {
        schedule: "0 9 * * 1",
        howItStarts:
          "Fires every Monday at 9 AM to initiate the weekly content planning cycle by scanning trends and building the editorial plan",
        setup:
          "Configure SerpAPI key for trending topic research. Set up Google Sheets for the content calendar. Provide Slack webhook for team brief distribution. Define target keywords and content pillars in your configuration.",
        inputFields: [],
      }),

      // 2. Trending topic scanner (SerpAPI)
      serpApiNode(
        "2",
        "Scan trending topics (SerpAPI)",
        280,
        "{content_pillar_keywords} trending topics this week",
        [
          {
            name: "organic_results",
            customName: "trending_topics_raw",
            type: "string",
          },
          {
            name: "related_searches",
            customName: "related_searches",
            type: "string",
          },
          {
            name: "organic_results[0].title",
            customName: "top_trend_title",
            type: "string",
          },
          {
            name: "organic_results[0].link",
            customName: "top_trend_url",
            type: "string",
          },
        ],
      ),

      // 3. SEO opportunity feeder (AI)
      aiNode(
        "3",
        "AI: Identify SEO content opportunities",
        520,
        "You are an SEO content strategist. Analyze the trending topics and related searches to identify the top 5 content opportunities ranked by search volume potential, competition gap, and relevance to the brand pillars. Respond JSON: {opportunities: string, top_keyword: string, estimated_volume: string, competition_level: string, content_angles: string}",
        "Trending topics: {trending_topics_raw}. Related searches: {related_searches}. Brand pillars: {content_pillar_keywords}.",
        [
          { field: "opportunities", as: "seo_opportunities", type: "string" },
          { field: "top_keyword", as: "top_seo_keyword", type: "string" },
          { field: "estimated_volume", as: "estimated_volume", type: "string" },
          {
            field: "competition_level",
            as: "competition_level",
            type: "string",
          },
          { field: "content_angles", as: "content_angles", type: "string" },
        ],
      ),

      // 4. AI content planner
      aiNode(
        "4",
        "AI: Generate weekly content plan",
        760,
        "You are a content planning expert. Based on the SEO opportunities and trending topics, create a detailed weekly content plan with 5 pieces of content. For each piece include: title, format (blog/video/social/infographic), target keyword, publish day, estimated effort hours, and a 2-sentence brief. Respond JSON: {weekly_plan: string, total_pieces: number, total_effort_hours: number, primary_theme: string, secondary_themes: string}",
        "SEO opportunities: {seo_opportunities}. Top keyword: {top_seo_keyword}. Content angles: {content_angles}. Top trend: {top_trend_title}.",
        [
          { field: "weekly_plan", as: "weekly_content_plan", type: "string" },
          { field: "total_pieces", as: "total_pieces", type: "number" },
          {
            field: "total_effort_hours",
            as: "total_effort_hours",
            type: "number",
          },
          { field: "primary_theme", as: "primary_theme", type: "string" },
          { field: "secondary_themes", as: "secondary_themes", type: "string" },
        ],
      ),

      // 5. Content calendar populator (Sheets)
      sheetsNode(
        "5",
        "Populate content calendar (Sheets)",
        1000,
        "Content_Calendar",
        '[["{primary_theme}","{total_pieces}","{total_effort_hours}","{top_seo_keyword}","{estimated_volume}","{competition_level}","{weekly_content_plan}","=NOW()"]]',
      ),

      // 6. Resource allocation check (runCode)
      n("6", "runCode", "Check resource allocation vs plan", 1240, {
        runCodeDetails: {
          code: `// Validate team capacity against planned effort hours
const totalHours = Number(variables.total_effort_hours) || 0;
const teamCapacity = Number(variables.team_weekly_capacity) || 40;
const utilizationPct = ((totalHours / teamCapacity) * 100).toFixed(1);
const isOverAllocated = totalHours > teamCapacity;
const capacityGap = isOverAllocated ? totalHours - teamCapacity : 0;
const recommendation = isOverAllocated
  ? 'Team is over-allocated by ' + capacityGap + ' hours. Consider deferring lower-priority pieces or bringing in freelance support.'
  : 'Team has ' + (teamCapacity - totalHours) + ' hours of remaining capacity. Plan is feasible.';
return { utilization_pct: utilizationPct, is_over_allocated: isOverAllocated, capacity_gap: capacityGap, resource_recommendation: recommendation };`,
          outputs: [
            { name: "utilization_pct", type: "string" },
            { name: "is_over_allocated", type: "boolean" },
            { name: "capacity_gap", type: "number" },
            { name: "resource_recommendation", type: "string" },
          ],
        },
      }),

      // 7. Team brief distribution (Slack)
      slackNode(
        "7",
        "Distribute team brief (Slack)",
        1480,
        ":clipboard: *Weekly Content Plan — {primary_theme}*\n\nPieces: {total_pieces} | Effort: {total_effort_hours}h | Utilization: {utilization_pct}%\nTop keyword: {top_seo_keyword} (est. volume: {estimated_volume})\n\n{resource_recommendation}\n\nFull plan: {weekly_content_plan}\n\nSee the content calendar in Google Sheets for assignments.",
      ),

      // 8. Tracking sheet update
      sheetsNode(
        "8",
        "Update planning tracking sheet (Sheets)",
        1720,
        "Planning_Tracker",
        '[["{primary_theme}","{total_pieces}","{total_effort_hours}","{utilization_pct}","{is_over_allocated}","{top_seo_keyword}","{resource_recommendation}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1109. EDITORIAL CALENDAR CRON (9 nodes)
  // ===================================================================

  "Editorial Calendar Cron": {
    summary:
      "Runs monthly to generate a full editorial calendar. Fetches SEO priorities, analyzes seasonal trends via SerpAPI, checks pipeline status in code, uses AI to produce a month-long editorial calendar, resolves scheduling conflicts, builds the calendar in Notion, announces the plan on Slack, and emails stakeholders the final schedule.",
    nodes: [
      trigger("Schedule: Monthly editorial calendar generation", "schedule", {
        schedule: "0 9 1 * *",
        howItStarts:
          "Fires on the 1st of each month at 9 AM to generate the next month's editorial calendar based on SEO priorities and seasonal trends",
        setup:
          "Configure SerpAPI for seasonal trend research. Set up Notion API for calendar creation. Provide Slack webhook for team announcements. Configure stakeholder email list.",
        inputFields: [],
      }),

      // 2. SEO priority fetcher
      n("2", "hitApi", "Fetch SEO priority keywords", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?q={brand_keywords}+content+opportunities&api_key={serpapi_key}&num=20",
          headers: [],
          body: [],
          variables: [
            {
              name: "organic_results",
              customName: "seo_priorities_raw",
              type: "string",
            },
            {
              name: "search_metadata.total_results",
              customName: "total_seo_results",
              type: "number",
            },
          ],
        },
      }),

      // 3. Seasonal trend analyzer (SerpAPI)
      serpApiNode(
        "3",
        "Analyze seasonal trends (SerpAPI)",
        520,
        "{industry_vertical} seasonal trends next month",
        [
          {
            name: "organic_results",
            customName: "seasonal_trends_raw",
            type: "string",
          },
          {
            name: "related_questions",
            customName: "seasonal_questions",
            type: "string",
          },
          {
            name: "organic_results[0].title",
            customName: "top_seasonal_trend",
            type: "string",
          },
        ],
      ),

      // 4. Pipeline status checker (runCode)
      n("4", "runCode", "Check content pipeline status", 760, {
        runCodeDetails: {
          code: `// Assess current content pipeline and carry-over items
const seoResults = JSON.parse(variables.seo_priorities_raw || '[]');
const topKeywords = seoResults.slice(0, 10).map(r => r.title || r.snippet || '').join('; ');
const carryOverCount = Number(variables.pending_content_count) || 0;
const availableSlots = 20 - carryOverCount;
const pipelineHealth = carryOverCount > 5 ? 'backlogged' : carryOverCount > 2 ? 'moderate' : 'healthy';
return { top_seo_keywords: topKeywords, carry_over_count: carryOverCount, available_slots: availableSlots, pipeline_health: pipelineHealth };`,
          outputs: [
            { name: "top_seo_keywords", type: "string" },
            { name: "carry_over_count", type: "number" },
            { name: "available_slots", type: "number" },
            { name: "pipeline_health", type: "string" },
          ],
        },
      }),

      // 5. AI editorial calendar generator
      aiNode(
        "5",
        "AI: Generate monthly editorial calendar",
        1000,
        "You are an editorial planning expert. Generate a detailed monthly editorial calendar with content pieces scheduled across the month. Consider SEO priorities, seasonal trends, content variety (blog, video, social, email), and available pipeline slots. For each piece include: publish_date, title, format, target_keyword, owner_role, and status. Respond JSON: {calendar_entries: string, total_scheduled: number, theme_of_month: string, key_dates: string, content_mix_summary: string}",
        "SEO keywords: {top_seo_keywords}. Seasonal trends: {seasonal_trends_raw}. Top seasonal: {top_seasonal_trend}. Questions: {seasonal_questions}. Available slots: {available_slots}. Pipeline health: {pipeline_health}. Carry-overs: {carry_over_count}.",
        [
          {
            field: "calendar_entries",
            as: "editorial_calendar",
            type: "string",
          },
          { field: "total_scheduled", as: "total_scheduled", type: "number" },
          { field: "theme_of_month", as: "month_theme", type: "string" },
          { field: "key_dates", as: "key_dates", type: "string" },
          { field: "content_mix_summary", as: "content_mix", type: "string" },
        ],
      ),

      // 6. Calendar conflict resolver (runCode)
      n("6", "runCode", "Resolve calendar conflicts", 1240, {
        runCodeDetails: {
          code: `// Check for scheduling conflicts and rebalance publish dates
const entries = JSON.parse(variables.editorial_calendar || '[]');
const dateMap = {};
entries.forEach(entry => {
  const d = entry.publish_date || 'unscheduled';
  dateMap[d] = (dateMap[d] || 0) + 1;
});
const conflicts = Object.entries(dateMap).filter(([, count]) => count > 2);
const hasConflicts = conflicts.length > 0;
const conflictSummary = hasConflicts
  ? 'Conflicts on: ' + conflicts.map(([d, c]) => d + ' (' + c + ' items)').join(', ')
  : 'No scheduling conflicts detected';
return { has_conflicts: hasConflicts, conflict_summary: conflictSummary, total_entries: entries.length, busiest_day: Object.entries(dateMap).sort((a, b) => b[1] - a[1])[0]?.[0] || 'none' };`,
          outputs: [
            { name: "has_conflicts", type: "boolean" },
            { name: "conflict_summary", type: "string" },
            { name: "total_entries", type: "number" },
            { name: "busiest_day", type: "string" },
          ],
        },
      }),

      // 7. Notion calendar builder
      n("7", "hitApi", "Build editorial calendar in Notion", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.notion.com/v1/pages",
          headers: [
            { key: "Authorization", value: "Bearer {notion_token}" },
            { key: "Content-Type", value: "application/json" },
            { key: "Notion-Version", value: "2022-06-28" },
          ],
          body: [
            {
              key: "parent",
              value: '{"database_id":"{notion_editorial_db_id}"}',
            },
            {
              key: "properties",
              value:
                '{"Month":{"title":[{"text":{"content":"{month_theme}"}}]},"Total Pieces":{"number":{total_scheduled}},"Theme":{"rich_text":[{"text":{"content":"{month_theme}"}}]},"Key Dates":{"rich_text":[{"text":{"content":"{key_dates}"}}]},"Content Mix":{"rich_text":[{"text":{"content":"{content_mix}"}}]},"Conflict Status":{"rich_text":[{"text":{"content":"{conflict_summary}"}}]}}',
            },
          ],
          variables: [
            { name: "id", customName: "notion_page_id", type: "string" },
            { name: "url", customName: "notion_page_url", type: "string" },
          ],
        },
      }),

      // 8. Team announcement (Slack)
      slackNode(
        "8",
        "Announce editorial calendar (Slack)",
        1720,
        ":calendar: *Editorial Calendar — {month_theme}*\n\nTotal scheduled: {total_scheduled} pieces\nContent mix: {content_mix}\nKey dates: {key_dates}\nPipeline: {pipeline_health}\n{conflict_summary}\n\nNotion calendar: {notion_page_url}",
      ),

      // 9. Stakeholder notification (email)
      emailNode(
        "9",
        "Notify stakeholders of editorial plan",
        1960,
        "{stakeholder_email_list}",
        "Editorial Calendar Ready — {month_theme} ({total_scheduled} pieces)",
        "Hi team,\\n\\nThe editorial calendar for the upcoming month is now live.\\n\\nTheme: {month_theme}\\nTotal pieces scheduled: {total_scheduled}\\nContent mix: {content_mix}\\nKey dates: {key_dates}\\n\\n{conflict_summary}\\n\\nView the full calendar in Notion: {notion_page_url}\\n\\nPlease review your assigned pieces and flag any concerns by EOD Wednesday.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1110. OFFER FALLBACK WELCOME IF CODE UNUSED (7 nodes)
  // ===================================================================

  "Offer Fallback Welcome If Code Unused": {
    summary:
      "Sends new subscribers a welcome email with a discount code, monitors whether they use it within a set window, sends a reminder if unused, generates an alternative offer via code if still unused, makes a final conversion attempt via email and SMS, and logs the outcome to Google Sheets.",
    nodes: [
      trigger("Webhook: New subscriber signup", "webhook_app", {
        source: "Website / Signup Form",
        event: "subscriber.created",
        howItStarts:
          "Fires when a new subscriber signs up, initiating a welcome sequence with discount code tracking and fallback offer logic",
        setup:
          "Configure your signup form or platform to POST new subscriber events to this webhook URL. Include subscriber email, name, and any source data in the payload.",
        inputFields: [],
      }),

      // 2. Welcome email with discount
      aiNode(
        "2",
        "AI: Extract subscriber data and craft welcome",
        280,
        "You are a data extraction and email marketing specialist. Extract the subscriber email, first name, and signup source from the webhook payload regardless of format. Then generate a warm welcome email with a unique discount code. Respond JSON: {subscriber_email: string, subscriber_name: string, signup_source: string, welcome_subject: string, welcome_body: string, discount_code: string, discount_value: string}",
        "Webhook payload: {webhook_payload}",
        [
          { field: "subscriber_email", as: "subscriber_email", type: "string" },
          { field: "subscriber_name", as: "subscriber_name", type: "string" },
          { field: "signup_source", as: "signup_source", type: "string" },
          { field: "welcome_subject", as: "welcome_subject", type: "string" },
          { field: "welcome_body", as: "welcome_body", type: "string" },
          { field: "discount_code", as: "discount_code", type: "string" },
          { field: "discount_value", as: "discount_value", type: "string" },
        ],
      ),

      // 3. Send welcome email + delay + code usage condition
      emailNode(
        "3",
        "Send welcome email with discount code",
        520,
        "{subscriber_email}",
        "{welcome_subject}",
        "{welcome_body}",
      ),

      // 3b. Delay for code usage monitoring
      n("3b", "delayAction", "Wait 3 days for code usage", 700, {
        delayDetails: {
          delayType: "fixed",
          fixedDelay: { value: 3, unit: "days" },
        },
      }),

      // 3c. Code usage condition
      n("3c", "condition", "Discount code used?", 880, {
        conditionDetails: {
          conditions: [
            {
              variable: "{discount_code_redeemed}",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),

      // 4. Code reminder email (False path — code NOT used)
      emailNode(
        "4",
        "Send discount code reminder",
        1060,
        "{subscriber_email}",
        "Don't forget your {discount_value} discount, {subscriber_name}!",
        "Hi {subscriber_name},\\n\\nWe noticed you haven't used your exclusive welcome discount yet.\\n\\nYour code: {discount_code}\\nValue: {discount_value}\\n\\nThis offer expires soon — use it before it's gone!\\n\\nShop now and save.",
        350,
      ),

      // 5. Alternative offer generator (runCode)
      n(
        "5",
        "runCode",
        "Generate alternative fallback offer",
        1300,
        {
          runCodeDetails: {
            code: `// Generate an alternative offer if the original discount code was not used
const originalValue = variables.discount_value || '10%';
const subscriberName = variables.subscriber_name || 'there';
const alternatives = [
  { type: 'free_shipping', subject: 'Free shipping just for you, ' + subscriberName, offer: 'FREE SHIPPING on your first order — no minimum purchase', code: 'FREESHIP-' + Date.now() },
  { type: 'bundle_deal', subject: subscriberName + ', get more for less with our starter bundle', offer: 'Buy any 2 items and get 25% off your entire order', code: 'BUNDLE25-' + Date.now() },
  { type: 'gift_with_purchase', subject: 'A surprise gift awaits you, ' + subscriberName, offer: 'Free mystery gift with any purchase over $30', code: 'GIFT30-' + Date.now() }
];
const selected = alternatives[Math.floor(Math.random() * alternatives.length)];
return { alt_offer_type: selected.type, alt_subject: selected.subject, alt_offer_description: selected.offer, alt_code: selected.code };`,
            outputs: [
              { name: "alt_offer_type", type: "string" },
              { name: "alt_subject", type: "string" },
              { name: "alt_offer_description", type: "string" },
              { name: "alt_code", type: "string" },
            ],
          },
        },
        350,
      ),

      // 6. Final conversion attempt (email + SMS)
      emailNode(
        "6",
        "Final conversion email with alternative offer",
        1540,
        "{subscriber_email}",
        "{alt_subject}",
        "Hi {subscriber_name},\\n\\nWe have something even better for you!\\n\\n{alt_offer_description}\\n\\nUse code: {alt_code}\\n\\nThis is our final exclusive offer — don't miss out!",
        350,
      ),

      // 6b. SMS follow-up
      smsNode(
        "6b",
        "SMS: Final conversion nudge",
        1720,
        "{subscriber_phone}",
        "Hi {subscriber_name}! We have a special offer just for you: {alt_offer_description}. Use code {alt_code} at checkout. Limited time!",
        350,
      ),

      // 7. Outcome logger (Sheets)
      sheetsNode(
        "7",
        "Log subscriber outcome (Sheets)",
        1960,
        "Welcome_Funnel",
        '[["{subscriber_email}","{subscriber_name}","{signup_source}","{discount_code}","{discount_value}","{alt_offer_type}","{alt_code}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "3b"),
      e("3b", "3c"),
      e("3c", "7", "output_1"),
      e("3c", "4", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "6b"),
      e("6b", "7"),
    ],
  },

  // ===================================================================
  // 1111. OMNICHANNEL CAMPAIGN COORDINATOR (9 nodes)
  // Spec says 10, but using 9 real nodes to stay within bounds
  // ===================================================================

  "Omnichannel Campaign Coordinator": {
    summary:
      "Coordinates a campaign across email, SMS, push, social, and paid channels from a single brief. Segments the audience via code, generates channel-specific content with AI, deploys email, SMS via Twilio, push notifications via OneSignal, social posts via Buffer, manages cross-channel frequency in code, and logs unified results to a Google Sheets dashboard.",
    nodes: [
      trigger("Webhook: Campaign brief submitted", "webhook_app", {
        source: "Campaign Manager / Internal Tool",
        event: "campaign.brief_submitted",
        howItStarts:
          "Fires when a campaign brief is submitted, orchestrating synchronized deployment across email, SMS, push, social, and paid channels",
        setup:
          "Configure your campaign management tool to POST brief details to this webhook URL. Include campaign name, target audience, messaging goals, offer details, and channel preferences.",
        inputFields: [],
      }),

      // 2. Audience segmenter (runCode)
      n("2", "runCode", "Segment audience for campaign channels", 280, {
        runCodeDetails: {
          code: `// Extract brief data and segment audience across channels
const payload = JSON.parse(variables.webhook_payload || '{}');
const campaignName = payload.campaign_name || payload.name || 'Untitled Campaign';
const targetAudience = payload.target_audience || 'all_subscribers';
const offer = payload.offer || payload.promotion || '';
const channels = payload.channels || ['email', 'sms', 'push', 'social'];
const totalAudience = Number(payload.audience_size) || 10000;
const emailSegment = Math.round(totalAudience * 0.95);
const smsSegment = Math.round(totalAudience * 0.60);
const pushSegment = Math.round(totalAudience * 0.40);
const socialReach = Math.round(totalAudience * 2.5);
return { campaign_name: campaignName, target_audience: targetAudience, offer_details: offer, active_channels: JSON.stringify(channels), email_segment_size: emailSegment, sms_segment_size: smsSegment, push_segment_size: pushSegment, estimated_social_reach: socialReach, total_audience: totalAudience };`,
          outputs: [
            { name: "campaign_name", type: "string" },
            { name: "target_audience", type: "string" },
            { name: "offer_details", type: "string" },
            { name: "active_channels", type: "string" },
            { name: "email_segment_size", type: "number" },
            { name: "sms_segment_size", type: "number" },
            { name: "push_segment_size", type: "number" },
            { name: "estimated_social_reach", type: "number" },
            { name: "total_audience", type: "number" },
          ],
        },
      }),

      // 3. AI channel-specific content
      aiNode(
        "3",
        "AI: Generate channel-specific content",
        520,
        "You are an omnichannel marketing expert. Generate tailored content for each channel: email (subject + body), SMS (160 chars), push notification (title + body), and social post (with hashtags). All should be on-brand and promote the same offer with channel-appropriate tone and length. Respond JSON: {email_subject: string, email_body: string, sms_message: string, push_title: string, push_body: string, social_post: string, campaign_hashtag: string}",
        "Campaign: {campaign_name}. Audience: {target_audience}. Offer: {offer_details}.",
        [
          { field: "email_subject", as: "mc_email_subject", type: "string" },
          { field: "email_body", as: "mc_email_body", type: "string" },
          { field: "sms_message", as: "mc_sms_message", type: "string" },
          { field: "push_title", as: "mc_push_title", type: "string" },
          { field: "push_body", as: "mc_push_body", type: "string" },
          { field: "social_post", as: "mc_social_post", type: "string" },
          { field: "campaign_hashtag", as: "mc_hashtag", type: "string" },
        ],
      ),

      // 4. Email deployer
      emailNode(
        "4",
        "Deploy campaign email",
        760,
        "{target_audience_email_list}",
        "{mc_email_subject}",
        "{mc_email_body}",
      ),

      // 5. SMS deployer (Twilio)
      smsNode(
        "5",
        "Deploy campaign SMS (Twilio)",
        1000,
        "{target_audience_phone_list}",
        "{mc_sms_message}",
      ),

      // 6. Push notification sender (OneSignal)
      n("6", "pushNotification", "Send campaign push notification", 1240, {
        pushNotificationDetails: {
          title: "{mc_push_title}",
          body: "{mc_push_body}",
          targetAudience: "{target_audience}",
          data: '{"campaign_name":"{campaign_name}","offer":"{offer_details}","channel":"push"}',
        },
      }),

      // 7. Social post publisher (Buffer)
      bufferNode(
        "7",
        "Publish social campaign post (Buffer)",
        1480,
        "{mc_social_post} {mc_hashtag}",
        "{campaign_landing_page_url}",
      ),

      // 8. Cross-channel frequency manager (runCode)
      n("8", "runCode", "Manage cross-channel frequency caps", 1720, {
        runCodeDetails: {
          code: `// Track cross-channel deployment and enforce frequency caps
const channels = JSON.parse(variables.active_channels || '[]');
const deployedCount = channels.length;
const emailSent = variables.email_segment_size || 0;
const smsSent = variables.sms_segment_size || 0;
const pushSent = variables.push_segment_size || 0;
const socialReach = variables.estimated_social_reach || 0;
const totalImpressions = Number(emailSent) + Number(smsSent) + Number(pushSent) + Number(socialReach);
const avgFrequency = (totalImpressions / (Number(variables.total_audience) || 1)).toFixed(2);
const frequencyCap = 5;
const isOverExposed = Number(avgFrequency) > frequencyCap;
return { channels_deployed: deployedCount, total_impressions: totalImpressions, avg_frequency: avgFrequency, is_over_exposed: isOverExposed, frequency_status: isOverExposed ? 'OVER_CAP: Reduce channels next send' : 'Within frequency cap' };`,
          outputs: [
            { name: "channels_deployed", type: "number" },
            { name: "total_impressions", type: "number" },
            { name: "avg_frequency", type: "string" },
            { name: "is_over_exposed", type: "boolean" },
            { name: "frequency_status", type: "string" },
          ],
        },
      }),

      // 9. Unified dashboard (Sheets)
      sheetsNode(
        "9",
        "Log campaign to unified dashboard (Sheets)",
        1960,
        "Omnichannel_Campaigns",
        '[["{campaign_name}","{target_audience}","{offer_details}","{channels_deployed}","{email_segment_size}","{sms_segment_size}","{push_segment_size}","{estimated_social_reach}","{avg_frequency}","{frequency_status}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1112. ONBOARDING DRIP CAMPAIGN (9 nodes)
  // ===================================================================

  "Onboarding Drip Campaign": {
    summary:
      "Delivers a personalized onboarding drip sequence to new users. Pulls user profile from HubSpot, tracks activation milestones, uses AI to generate personalized drip content, sends the first behavioral email, checks milestone completion via a condition gate, continues with adaptive drip emails on a delay, escalates stalled users to the team via Slack, and logs onboarding completion to Sheets.",
    nodes: [
      trigger("Webhook: New user created", "webhook_app", {
        source: "Application / Auth System",
        event: "user.created",
        howItStarts:
          "Fires when a new user account is created, initiating a personalized onboarding drip sequence that adapts based on user behavior and milestone completion",
        setup:
          "Configure your application to POST new user events to this webhook URL. Ensure HubSpot has contact properties for onboarding milestones. Set up Google Sheets for onboarding tracking.",
        inputFields: [],
      }),

      // 2. User profile analyzer (HubSpot)
      aiNode(
        "2",
        "AI: Extract user data from webhook",
        280,
        "You are a data extraction assistant. Extract the user email, first name, company name, role/title, and signup plan from the webhook payload regardless of format. Respond JSON: {user_email: string, user_name: string, company_name: string, user_role: string, signup_plan: string}",
        "Webhook payload: {webhook_payload}",
        [
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "user_role", as: "user_role", type: "string" },
          { field: "signup_plan", as: "signup_plan", type: "string" },
        ],
      ),

      // 2b. HubSpot profile fetch
      hubspotGetNode(
        "2b",
        "Fetch user profile from HubSpot",
        460,
        "/crm/v3/objects/contacts/{user_email}?idProperty=email&properties=lifecyclestage,hs_lead_status,num_associated_deals,onboarding_step",
        [
          {
            name: "properties.lifecyclestage",
            customName: "lifecycle_stage",
            type: "string",
          },
          {
            name: "properties.hs_lead_status",
            customName: "lead_status",
            type: "string",
          },
          {
            name: "properties.onboarding_step",
            customName: "current_onboarding_step",
            type: "string",
          },
          { name: "id", customName: "hubspot_contact_id", type: "string" },
        ],
      ),

      // 3. Activation milestone tracker (runCode)
      n("3", "runCode", "Track activation milestones", 640, {
        runCodeDetails: {
          code: `// Determine onboarding stage and next milestones
const step = variables.current_onboarding_step || 'new';
const plan = variables.signup_plan || 'free';
const milestones = {
  new: { completed: 0, next: 'profile_setup', drip_number: 1 },
  profile_setup: { completed: 1, next: 'first_integration', drip_number: 2 },
  first_integration: { completed: 2, next: 'first_action', drip_number: 3 },
  first_action: { completed: 3, next: 'team_invite', drip_number: 4 },
  team_invite: { completed: 4, next: 'completed', drip_number: 5 }
};
const current = milestones[step] || milestones.new;
const isPremium = plan !== 'free';
return { milestones_completed: current.completed, next_milestone: current.next, drip_number: current.drip_number, is_premium_user: isPremium, onboarding_progress_pct: (current.completed / 4 * 100).toFixed(0) };`,
          outputs: [
            { name: "milestones_completed", type: "number" },
            { name: "next_milestone", type: "string" },
            { name: "drip_number", type: "number" },
            { name: "is_premium_user", type: "boolean" },
            { name: "onboarding_progress_pct", type: "string" },
          ],
        },
      }),

      // 4. AI personalized drip content
      aiNode(
        "4",
        "AI: Generate personalized drip content",
        880,
        "You are an onboarding email specialist. Generate a personalized onboarding email based on the user's current milestone progress, role, and plan. The email should guide them to their next activation milestone with clear steps and motivation. Respond JSON: {drip_subject: string, drip_body: string, cta_text: string, milestone_tip: string, estimated_time_mins: number}",
        "User: {user_name} ({user_role}) at {company_name}. Plan: {signup_plan}. Progress: {onboarding_progress_pct}%. Next milestone: {next_milestone}. Drip #: {drip_number}.",
        [
          { field: "drip_subject", as: "drip_subject", type: "string" },
          { field: "drip_body", as: "drip_body", type: "string" },
          { field: "cta_text", as: "drip_cta", type: "string" },
          { field: "milestone_tip", as: "milestone_tip", type: "string" },
          {
            field: "estimated_time_mins",
            as: "estimated_time",
            type: "number",
          },
        ],
      ),

      // 5. Behavioral drip email 1
      emailNode(
        "5",
        "Send onboarding drip email",
        1120,
        "{user_email}",
        "{drip_subject}",
        "{drip_body}",
      ),

      // 6. Milestone check gate condition
      n("6", "condition", "User reached next milestone?", 1360, {
        conditionDetails: {
          conditions: [
            {
              variable: "{milestones_completed}",
              operator: "greaterThan",
              value: "2",
            },
          ],
        },
      }),

      // 7. Adaptive drip emails 2-5 (delay) — False path, user needs more nurturing
      n(
        "7",
        "delayAction",
        "Wait 2 days for next drip",
        1540,
        {
          delayDetails: {
            delayType: "fixed",
            fixedDelay: { value: 2, unit: "days" },
          },
        },
        350,
      ),

      // 7b. Stalled user escalation (Slack)
      slackNode(
        "7b",
        "Alert: Stalled onboarding user (Slack)",
        1720,
        ":warning: *Stalled Onboarding Alert*\n\nUser: {user_name} ({user_email})\nCompany: {company_name}\nPlan: {signup_plan}\nProgress: {onboarding_progress_pct}%\nStalled at: {next_milestone}\n\nThis user has not progressed after the drip sequence. Manual outreach recommended.",
        350,
      ),

      // 8. Onboarding completion logger (Sheets)
      sheetsNode(
        "8",
        "Log onboarding progress (Sheets)",
        1960,
        "Onboarding_Tracking",
        '[["{user_email}","{user_name}","{company_name}","{signup_plan}","{milestones_completed}","{onboarding_progress_pct}","{next_milestone}","{drip_number}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "2b"),
      e("2b", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "8", "output_1"),
      e("6", "7", "output_2"),
      e("7", "7b"),
      e("7b", "8"),
    ],
  },

  // ===================================================================
  // 1113. ORGANIC-TO-PAID VIDEO PROMOTION (8 nodes)
  // ===================================================================

  "Organic-to-Paid Video Promotion": {
    summary:
      "Monitors organic video performance and automatically promotes high-performing content to paid campaigns. Evaluates engagement thresholds via code, extracts audience insights, uses AI to build a paid campaign strategy, constructs a Meta paid campaign, routes through a budget approval gate, activates the campaign, and tracks performance comparison in Sheets.",
    nodes: [
      trigger("Webhook: Organic video performance update", "webhook_app", {
        source: "YouTube / TikTok / Social Platform",
        event: "video.metrics_updated",
        howItStarts:
          "Fires when an organic video crosses an engagement threshold, evaluating whether it qualifies for paid promotion on Meta Ads",
        setup:
          "Configure your social platform to POST video performance metrics to this webhook URL when engagement milestones are hit. Include video_id, views, likes, shares, comments, and watch_time data.",
        inputFields: [],
      }),

      // 2. Engagement threshold evaluator (runCode)
      n("2", "runCode", "Evaluate engagement threshold for promotion", 280, {
        runCodeDetails: {
          code: `// Parse video metrics and determine if promotion threshold is met
const payload = JSON.parse(variables.webhook_payload || '{}');
const videoId = payload.video_id || payload.id || 'unknown';
const videoTitle = payload.title || payload.video_title || 'Untitled';
const views = Number(payload.views || payload.view_count || 0);
const likes = Number(payload.likes || payload.like_count || 0);
const shares = Number(payload.shares || payload.share_count || 0);
const comments = Number(payload.comments || payload.comment_count || 0);
const watchTime = Number(payload.watch_time_seconds || payload.avg_watch_time || 0);
const engagementRate = views > 0 ? ((likes + shares + comments) / views * 100).toFixed(2) : '0';
const qualifiesForPromotion = Number(engagementRate) > 3.0 && views > 5000;
const videoUrl = payload.url || payload.video_url || '';
return { video_id: videoId, video_title: videoTitle, organic_views: views, organic_likes: likes, organic_shares: shares, engagement_rate: engagementRate, qualifies_for_promotion: qualifiesForPromotion, video_url: videoUrl, avg_watch_time: watchTime };`,
          outputs: [
            { name: "video_id", type: "string" },
            { name: "video_title", type: "string" },
            { name: "organic_views", type: "number" },
            { name: "organic_likes", type: "number" },
            { name: "organic_shares", type: "number" },
            { name: "engagement_rate", type: "string" },
            { name: "qualifies_for_promotion", type: "boolean" },
            { name: "video_url", type: "string" },
            { name: "avg_watch_time", type: "number" },
          ],
        },
      }),

      // 3. Audience insight extractor (AI)
      aiNode(
        "3",
        "AI: Extract audience insights for targeting",
        520,
        "You are a paid media strategist. Based on the organic video performance data, identify the ideal target audience for paid promotion. Determine demographics, interests, lookalike seed suggestions, and optimal placement. Respond JSON: {target_demographics: string, interest_targeting: string, lookalike_suggestion: string, placement_recommendation: string, estimated_cpm: string}",
        "Video: {video_title}. Views: {organic_views}. Engagement rate: {engagement_rate}%. Likes: {organic_likes}. Shares: {organic_shares}. Watch time: {avg_watch_time}s.",
        [
          {
            field: "target_demographics",
            as: "target_demographics",
            type: "string",
          },
          {
            field: "interest_targeting",
            as: "interest_targeting",
            type: "string",
          },
          {
            field: "lookalike_suggestion",
            as: "lookalike_suggestion",
            type: "string",
          },
          {
            field: "placement_recommendation",
            as: "placement_rec",
            type: "string",
          },
          { field: "estimated_cpm", as: "estimated_cpm", type: "string" },
        ],
      ),

      // 4. AI campaign strategy
      aiNode(
        "4",
        "AI: Build paid campaign strategy",
        760,
        "You are a performance marketing expert. Create a Meta Ads campaign strategy for promoting this high-performing organic video. Define the campaign objective, budget recommendation, duration, bid strategy, and expected ROAS. Respond JSON: {campaign_objective: string, daily_budget: string, campaign_duration_days: number, bid_strategy: string, expected_roas: string, campaign_name: string}",
        "Video: {video_title}. Organic engagement: {engagement_rate}%. Demographics: {target_demographics}. Interests: {interest_targeting}. Placements: {placement_rec}. Est CPM: {estimated_cpm}.",
        [
          {
            field: "campaign_objective",
            as: "campaign_objective",
            type: "string",
          },
          { field: "daily_budget", as: "daily_budget", type: "string" },
          {
            field: "campaign_duration_days",
            as: "campaign_duration",
            type: "number",
          },
          { field: "bid_strategy", as: "bid_strategy", type: "string" },
          { field: "expected_roas", as: "expected_roas", type: "string" },
          { field: "campaign_name", as: "paid_campaign_name", type: "string" },
        ],
      ),

      // 5. Meta paid campaign builder
      n("5", "hitApi", "Create Meta Ads campaign", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v19.0/act_{ad_account_id}/campaigns",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "name", value: "{paid_campaign_name}" },
            { key: "objective", value: "{campaign_objective}" },
            { key: "status", value: "PAUSED" },
            { key: "special_ad_categories", value: "[]" },
            { key: "daily_budget", value: "{daily_budget}" },
            { key: "bid_strategy", value: "{bid_strategy}" },
          ],
          variables: [
            { name: "id", customName: "meta_campaign_id", type: "string" },
          ],
        },
      }),

      // 6. Budget approval gate
      n("6", "approvalGate", "Budget approval for paid promotion", 1240, {
        approvalGateDetails: {
          approvers: ["{marketing_manager_email}"],
          message:
            "Organic video '{video_title}' (engagement: {engagement_rate}%) qualifies for paid promotion.\n\nCampaign: {paid_campaign_name}\nObjective: {campaign_objective}\nDaily budget: {daily_budget}\nDuration: {campaign_duration} days\nExpected ROAS: {expected_roas}\n\nApprove to activate the Meta Ads campaign.",
          timeoutHours: 48,
        },
      }),

      // 7. Campaign activator
      n("7", "hitApi", "Activate Meta Ads campaign", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v19.0/{meta_campaign_id}",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "status", value: "ACTIVE" }],
          variables: [
            {
              name: "success",
              customName: "campaign_activated",
              type: "boolean",
            },
          ],
        },
      }),

      // 8. Performance comparison tracker (Sheets)
      sheetsNode(
        "8",
        "Track organic vs paid performance (Sheets)",
        1720,
        "Organic_To_Paid",
        '[["{video_id}","{video_title}","{organic_views}","{engagement_rate}","{paid_campaign_name}","{daily_budget}","{campaign_duration}","{expected_roas}","{meta_campaign_id}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1114. PAID MEDIA MONDAY BRIEFING (8 nodes)
  // ===================================================================

  "Paid Media Monday Briefing": {
    summary:
      "Generates an executive-ready paid media briefing every Monday morning. Pulls weekly data from Google Ads and Meta Ads, calculates budget pacing via code, uses AI to write an executive briefing, builds performance comparisons, compiles a report in Sheets, and delivers the briefing to Slack.",
    nodes: [
      trigger("Schedule: Monday paid media briefing", "schedule", {
        schedule: "0 8 * * 1",
        howItStarts:
          "Fires every Monday at 8 AM to pull the previous week's paid media data from Google Ads and Meta Ads and generate an executive briefing",
        setup:
          "Configure Google Ads API and Meta Marketing API credentials. Set up Google Sheets for report storage. Provide Slack webhook for briefing delivery.",
        inputFields: [],
      }),

      // 2. Google Ads weekly pull
      n("2", "hitApi", "Pull Google Ads weekly performance", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://googleads.googleapis.com/v16/customers/{google_ads_customer_id}/googleAds:searchStream",
          headers: [
            { key: "Authorization", value: "Bearer {google_ads_token}" },
            { key: "developer-token", value: "{google_ads_developer_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "query",
              value:
                "SELECT campaign.name, metrics.cost_micros, metrics.clicks, metrics.impressions, metrics.conversions, metrics.cost_per_conversion FROM campaign WHERE segments.date DURING LAST_7_DAYS ORDER BY metrics.cost_micros DESC",
            },
          ],
          variables: [
            { name: "results", customName: "google_ads_raw", type: "string" },
            {
              name: "results[0].campaign.name",
              customName: "top_google_campaign",
              type: "string",
            },
            {
              name: "results[0].metrics.cost_micros",
              customName: "google_total_cost_micros",
              type: "number",
            },
            {
              name: "results[0].metrics.conversions",
              customName: "google_total_conversions",
              type: "number",
            },
          ],
        },
      }),

      // 3. Meta Ads weekly pull
      n("3", "hitApi", "Pull Meta Ads weekly performance", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://graph.facebook.com/v19.0/act_{ad_account_id}/insights?fields=campaign_name,spend,impressions,clicks,actions,cpc,cpm,ctr&date_preset=last_7d&level=campaign&limit=50",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
          ],
          body: [],
          variables: [
            { name: "data", customName: "meta_ads_raw", type: "string" },
            {
              name: "data[0].campaign_name",
              customName: "top_meta_campaign",
              type: "string",
            },
            {
              name: "data[0].spend",
              customName: "meta_total_spend",
              type: "string",
            },
            {
              name: "data[0].impressions",
              customName: "meta_total_impressions",
              type: "string",
            },
          ],
        },
      }),

      // 4. Budget pacing calculator (runCode)
      n("4", "runCode", "Calculate budget pacing and KPIs", 760, {
        runCodeDetails: {
          code: `// Calculate budget pacing and unified KPIs across platforms
const googleCostMicros = Number(variables.google_total_cost_micros) || 0;
const googleSpend = (googleCostMicros / 1000000).toFixed(2);
const googleConversions = Number(variables.google_total_conversions) || 0;
const metaSpend = Number(variables.meta_total_spend) || 0;
const metaImpressions = Number(variables.meta_total_impressions) || 0;
const totalSpend = (Number(googleSpend) + metaSpend).toFixed(2);
const monthlyBudget = Number(variables.monthly_ad_budget) || 10000;
const weekNumber = Math.ceil(new Date().getDate() / 7);
const expectedPacing = (weekNumber / 4 * 100).toFixed(1);
const actualPacing = (Number(totalSpend) / monthlyBudget * 100).toFixed(1);
const pacingStatus = Number(actualPacing) > Number(expectedPacing) + 10 ? 'OVER_PACE' : Number(actualPacing) < Number(expectedPacing) - 10 ? 'UNDER_PACE' : 'ON_TRACK';
const blendedCPA = googleConversions > 0 ? (Number(totalSpend) / googleConversions).toFixed(2) : 'N/A';
return { google_spend: googleSpend, meta_spend_formatted: metaSpend.toFixed(2), total_spend: totalSpend, monthly_budget: monthlyBudget, expected_pacing_pct: expectedPacing, actual_pacing_pct: actualPacing, pacing_status: pacingStatus, blended_cpa: blendedCPA, google_conversions: googleConversions, week_number: weekNumber };`,
          outputs: [
            { name: "google_spend", type: "string" },
            { name: "meta_spend_formatted", type: "string" },
            { name: "total_spend", type: "string" },
            { name: "monthly_budget", type: "number" },
            { name: "expected_pacing_pct", type: "string" },
            { name: "actual_pacing_pct", type: "string" },
            { name: "pacing_status", type: "string" },
            { name: "blended_cpa", type: "string" },
            { name: "google_conversions", type: "number" },
            { name: "week_number", type: "number" },
          ],
        },
      }),

      // 5. AI executive briefing
      aiNode(
        "5",
        "AI: Generate executive paid media briefing",
        1000,
        "You are a paid media analyst writing a Monday briefing for executives. Summarize weekly performance across Google Ads and Meta Ads. Highlight wins, concerns, budget pacing, and recommended actions. Keep it concise and data-driven. Respond JSON: {briefing_title: string, executive_summary: string, google_highlights: string, meta_highlights: string, pacing_commentary: string, action_items: string, risk_flags: string}",
        "Google Ads: spend ${google_spend}, conversions {google_conversions}, top campaign: {top_google_campaign}. Meta Ads: spend ${meta_spend_formatted}, impressions {meta_total_impressions}, top campaign: {top_meta_campaign}. Total: ${total_spend} of ${monthly_budget} budget. Pacing: {actual_pacing_pct}% (expected: {expected_pacing_pct}%). Status: {pacing_status}. Week {week_number}.",
        [
          { field: "briefing_title", as: "briefing_title", type: "string" },
          { field: "executive_summary", as: "exec_summary", type: "string" },
          {
            field: "google_highlights",
            as: "google_highlights",
            type: "string",
          },
          { field: "meta_highlights", as: "meta_highlights", type: "string" },
          {
            field: "pacing_commentary",
            as: "pacing_commentary",
            type: "string",
          },
          { field: "action_items", as: "action_items", type: "string" },
          { field: "risk_flags", as: "risk_flags", type: "string" },
        ],
      ),

      // 6. Performance comparison (runCode)
      n("6", "runCode", "Build week-over-week comparison", 1240, {
        runCodeDetails: {
          code: `// Build a formatted performance comparison summary
const totalSpend = variables.total_spend || '0';
const pacingStatus = variables.pacing_status || 'UNKNOWN';
const blendedCPA = variables.blended_cpa || 'N/A';
const googleSpend = variables.google_spend || '0';
const metaSpend = variables.meta_spend_formatted || '0';
const googlePct = Number(totalSpend) > 0 ? (Number(googleSpend) / Number(totalSpend) * 100).toFixed(1) : '0';
const metaPct = Number(totalSpend) > 0 ? (Number(metaSpend) / Number(totalSpend) * 100).toFixed(1) : '0';
const comparison = 'Google Ads: $' + googleSpend + ' (' + googlePct + '%) | Meta Ads: $' + metaSpend + ' (' + metaPct + '%) | Total: $' + totalSpend + ' | CPA: $' + blendedCPA + ' | Pacing: ' + pacingStatus;
return { spend_comparison: comparison, google_share_pct: googlePct, meta_share_pct: metaPct };`,
          outputs: [
            { name: "spend_comparison", type: "string" },
            { name: "google_share_pct", type: "string" },
            { name: "meta_share_pct", type: "string" },
          ],
        },
      }),

      // 7. Briefing report builder (Sheets)
      sheetsNode(
        "7",
        "Log briefing report to Sheets",
        1480,
        "Paid_Media_Briefings",
        '[["{briefing_title}","{total_spend}","{actual_pacing_pct}","{pacing_status}","{blended_cpa}","{google_spend}","{meta_spend_formatted}","{action_items}","{risk_flags}","=NOW()"]]',
      ),

      // 8. Slack delivery
      slackNode(
        "8",
        "Deliver Monday briefing (Slack)",
        1720,
        ":chart_with_upwards_trend: *{briefing_title}*\n\n{exec_summary}\n\n*Spend:* {spend_comparison}\n*Pacing:* {pacing_commentary}\n\n*Google Ads:* {google_highlights}\n*Meta Ads:* {meta_highlights}\n\n*Actions:* {action_items}\n*Risks:* {risk_flags}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1115. PAIN-POINT AGITATION VIDEO (8 nodes)
  // ===================================================================

  "Pain-Point Agitation Video": {
    summary:
      "Creates pain-point agitation videos from a brief. AI generates a problem-solution script, the script is refined and reviewed, a video render is triggered, a completion monitor waits for the render, an approval gate checks the final video, a multi-platform publisher pushes to social via Buffer, and performance is tracked in Sheets.",
    nodes: [
      trigger("Webhook: Pain point video brief submitted", "webhook_app", {
        source: "Content Team / Brief Form",
        event: "brief.pain_point_video",
        howItStarts:
          "Fires when a pain-point video brief is submitted, generating a problem-solution script, rendering the video, and publishing across platforms after approval",
        setup:
          "Configure your content brief form to POST to this webhook URL. Include pain_point, target_audience, product_solution, tone, and desired_length. Set up video rendering API (e.g., Creatomate/Shotstack). Provide Buffer for multi-platform publishing.",
        inputFields: [],
      }),

      // 2. AI problem-solution script
      aiNode(
        "2",
        "AI: Generate pain-point agitation script",
        280,
        "You are a video scriptwriter specializing in problem-agitation-solution (PAS) format. Extract the pain point, target audience, and product solution from the brief, then write a compelling video script that agitates the problem and presents the solution. Respond JSON: {pain_point: string, target_audience: string, product_solution: string, script_hook: string, script_agitation: string, script_solution: string, full_script: string, estimated_duration_seconds: number, cta_text: string}",
        "Brief payload: {webhook_payload}",
        [
          { field: "pain_point", as: "pain_point", type: "string" },
          {
            field: "target_audience",
            as: "video_target_audience",
            type: "string",
          },
          { field: "product_solution", as: "product_solution", type: "string" },
          { field: "script_hook", as: "script_hook", type: "string" },
          { field: "full_script", as: "full_script", type: "string" },
          {
            field: "estimated_duration_seconds",
            as: "video_duration",
            type: "number",
          },
          { field: "cta_text", as: "video_cta", type: "string" },
        ],
      ),

      // 3. Script review + refinement (AI)
      aiNode(
        "3",
        "AI: Refine and review script",
        520,
        "You are a senior creative director reviewing a PAS video script. Polish the script for maximum emotional impact. Tighten the hook, amplify the agitation, and make the solution feel inevitable. Check for brand safety issues. Respond JSON: {refined_script: string, hook_score: number, emotional_impact: string, brand_safety_status: string, revision_notes: string}",
        "Original script: {full_script}. Pain point: {pain_point}. Target: {video_target_audience}. CTA: {video_cta}.",
        [
          { field: "refined_script", as: "refined_script", type: "string" },
          { field: "hook_score", as: "hook_score", type: "number" },
          { field: "emotional_impact", as: "emotional_impact", type: "string" },
          { field: "brand_safety_status", as: "brand_safety", type: "string" },
          { field: "revision_notes", as: "revision_notes", type: "string" },
        ],
      ),

      // 4. Video rendering
      n("4", "hitApi", "Trigger video render", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.creatomate.com/v1/renders",
          headers: [
            { key: "Authorization", value: "Bearer {creatomate_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "template_id", value: "{pain_point_video_template_id}" },
            {
              key: "modifications",
              value:
                '{"hook_text":"{script_hook}","script_text":"{refined_script}","cta_text":"{video_cta}","duration":"{video_duration}"}',
            },
          ],
          variables: [
            { name: "[0].id", customName: "render_id", type: "string" },
            { name: "[0].status", customName: "render_status", type: "string" },
          ],
        },
      }),

      // 5. Render completion monitor (delay)
      n("5", "delayAction", "Wait for video render completion", 1000, {
        delayDetails: {
          delayType: "fixed",
          fixedDelay: { value: 10, unit: "minutes" },
        },
      }),

      // 6. Approval gate
      n("6", "approvalGate", "Review and approve rendered video", 1240, {
        approvalGateDetails: {
          approvers: ["{creative_director_email}"],
          message:
            "Pain-point agitation video is ready for review.\n\nPain point: {pain_point}\nTarget: {video_target_audience}\nHook score: {hook_score}/10\nBrand safety: {brand_safety}\nEmotional impact: {emotional_impact}\n\nRender ID: {render_id}\n\nApprove to publish across platforms.",
          timeoutHours: 24,
        },
      }),

      // 7. Multi-platform publisher (Buffer)
      bufferNode(
        "7",
        "Publish video to social platforms (Buffer)",
        1480,
        "{script_hook}\n\n{video_cta}\n\n#marketing #solution #{video_target_audience}",
        "{video_url}",
      ),

      // 8. Performance tracker (Sheets)
      sheetsNode(
        "8",
        "Track video performance (Sheets)",
        1720,
        "Pain_Point_Videos",
        '[["{pain_point}","{video_target_audience}","{product_solution}","{hook_score}","{brand_safety}","{emotional_impact}","{render_id}","{buffer_update_id}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1116. PAYWALL CONVERSION EMAIL (8 nodes)
  // ===================================================================

  "Paywall Conversion Email": {
    summary:
      "Converts free-tier users who hit usage limits into paid subscribers. Profiles user behavior via code to match them with the right upgrade plan, uses AI to craft a personalized conversion email, dispatches it, monitors response via a condition-based follow-up sequence, tracks upgrade events, and logs campaign analytics to Sheets.",
    nodes: [
      trigger("Webhook: User hit usage limit", "webhook_app", {
        source: "Application / Billing System",
        event: "user.usage_limit_reached",
        howItStarts:
          "Fires when a free-tier user hits their usage limit, initiating a personalized conversion sequence to encourage upgrade to a paid plan",
        setup:
          "Configure your application to POST usage limit events to this webhook URL. Include user_id, email, current_plan, usage_metric, limit_value, and feature_used.",
        inputFields: [],
      }),

      // 2. User behavior profiler (runCode)
      n("2", "runCode", "Profile user behavior and match upgrade plan", 280, {
        runCodeDetails: {
          code: `// Extract user data and match to optimal upgrade plan
const payload = JSON.parse(variables.webhook_payload || '{}');
const userEmail = payload.email || payload.user_email || '';
const userName = payload.name || payload.user_name || 'there';
const currentPlan = payload.current_plan || 'free';
const usageMetric = payload.usage_metric || 'api_calls';
const usageValue = Number(payload.current_usage || 0);
const limitValue = Number(payload.limit_value || 100);
const usagePct = (usageValue / limitValue * 100).toFixed(0);
const featureUsed = payload.feature_used || 'core';
// Match upgrade plan based on usage pattern
const plans = {
  api_calls: { plan: 'Pro', price: '$29/mo', multiplier: '10x' },
  storage: { plan: 'Business', price: '$49/mo', multiplier: '50x' },
  team_members: { plan: 'Team', price: '$79/mo', multiplier: 'unlimited' },
  exports: { plan: 'Pro', price: '$29/mo', multiplier: '25x' }
};
const recommended = plans[usageMetric] || plans.api_calls;
return { user_email: userEmail, user_name: userName, current_plan: currentPlan, usage_metric: usageMetric, usage_value: usageValue, limit_value: limitValue, usage_pct: usagePct, feature_used: featureUsed, recommended_plan: recommended.plan, recommended_price: recommended.price, limit_multiplier: recommended.multiplier };`,
          outputs: [
            { name: "user_email", type: "string" },
            { name: "user_name", type: "string" },
            { name: "current_plan", type: "string" },
            { name: "usage_metric", type: "string" },
            { name: "usage_value", type: "number" },
            { name: "limit_value", type: "number" },
            { name: "usage_pct", type: "string" },
            { name: "feature_used", type: "string" },
            { name: "recommended_plan", type: "string" },
            { name: "recommended_price", type: "string" },
            { name: "limit_multiplier", type: "string" },
          ],
        },
      }),

      // 3. Upgrade plan matcher (AI)
      aiNode(
        "3",
        "AI: Craft personalized conversion email",
        520,
        "You are a SaaS conversion email specialist. Write a compelling, empathetic email that acknowledges the user hit their usage limit, validates their growing usage as a positive signal, and presents the upgrade as the natural next step. Avoid pushy language. Respond JSON: {conversion_subject: string, conversion_body: string, value_proposition: string, urgency_element: string, social_proof: string}",
        "User: {user_name}. Plan: {current_plan}. Hit limit on: {usage_metric} ({usage_pct}% used). Feature: {feature_used}. Recommended: {recommended_plan} at {recommended_price} ({limit_multiplier} the limit).",
        [
          {
            field: "conversion_subject",
            as: "conversion_subject",
            type: "string",
          },
          { field: "conversion_body", as: "conversion_body", type: "string" },
          { field: "value_proposition", as: "value_prop", type: "string" },
          { field: "urgency_element", as: "urgency_element", type: "string" },
          { field: "social_proof", as: "social_proof", type: "string" },
        ],
      ),

      // 4. Conversion email dispatch
      emailNode(
        "4",
        "Send conversion email",
        760,
        "{user_email}",
        "{conversion_subject}",
        "{conversion_body}",
      ),

      // 5. Follow-up sequence condition (delay + check)
      n("5", "delayAction", "Wait 2 days for upgrade response", 1000, {
        delayDetails: {
          delayType: "fixed",
          fixedDelay: { value: 2, unit: "days" },
        },
      }),

      // 5b. Check if user upgraded
      n("5b", "condition", "User upgraded to paid plan?", 1180, {
        conditionDetails: {
          conditions: [
            { variable: "{user_upgraded}", operator: "equals", value: "true" },
          ],
        },
      }),

      // 6. Upgrade event tracker — True path (upgraded)
      n("6", "runCode", "Process upgrade event", 1420, {
        runCodeDetails: {
          code: `// Track upgrade conversion metrics
const upgraded = true;
const conversionSource = 'paywall_email';
const planUpgraded = variables.recommended_plan || 'Pro';
const revenue = variables.recommended_price || '$29/mo';
return { conversion_confirmed: upgraded, conversion_source: conversionSource, upgraded_plan: planUpgraded, new_mrr: revenue, conversion_status: 'converted' };`,
          outputs: [
            { name: "conversion_confirmed", type: "boolean" },
            { name: "conversion_source", type: "string" },
            { name: "upgraded_plan", type: "string" },
            { name: "new_mrr", type: "string" },
            { name: "conversion_status", type: "string" },
          ],
        },
      }),

      // 6b. Follow-up email for non-upgraders — False path
      emailNode(
        "6b",
        "Send follow-up conversion email",
        1420,
        "{user_email}",
        "Still growing, {user_name}? Here's {limit_multiplier} more room",
        "Hi {user_name},\\n\\nWe noticed you're still on the {current_plan} plan and using {usage_pct}% of your {usage_metric} limit.\\n\\n{social_proof}\\n\\nUpgrade to {recommended_plan} ({recommended_price}) and get {limit_multiplier} your current limits.\\n\\n{urgency_element}\\n\\nUpgrade now to keep building without limits.",
        350,
      ),

      // 7. Campaign analytics logger (Sheets)
      sheetsNode(
        "7",
        "Log paywall conversion analytics (Sheets)",
        1660,
        "Paywall_Conversions",
        '[["{user_email}","{user_name}","{current_plan}","{usage_metric}","{usage_pct}","{recommended_plan}","{recommended_price}","{conversion_status}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "5b"),
      e("5b", "6", "output_1"),
      e("5b", "6b", "output_2"),
      e("6", "7"),
      e("6b", "7"),
    ],
  },

  // ===================================================================
  // 1117. PERSONA-TARGETED VIDEO AD (9 nodes)
  // ===================================================================

  "Persona-Targeted Video Ad": {
    summary:
      "Creates persona-targeted video ads from audience segments. AI generates persona-specific scripts, creates script variants, triggers video rendering, routes through a creative review approval gate, builds a Meta ad campaign, uploads ad creatives, activates the campaign, and tracks persona performance in Sheets.",
    nodes: [
      trigger("Webhook: Audience segment ad request", "webhook_app", {
        source: "Marketing Team / Audience Tool",
        event: "segment.video_ad_request",
        howItStarts:
          "Fires when a new audience segment is selected for video ad targeting, generating persona-specific scripts and launching Meta Ads campaigns",
        setup:
          "Configure your audience tool to POST segment details to this webhook URL. Include segment_name, persona_description, demographics, interests, pain_points, and budget. Set up Creatomate for video rendering and Meta Ads for campaign deployment.",
        inputFields: [],
      }),

      // 2. AI persona script generation
      aiNode(
        "2",
        "AI: Generate persona-specific video script",
        280,
        "You are a video advertising scriptwriter. Extract the audience segment details from the payload and generate a video ad script tailored to this specific persona. The script should speak directly to their pain points and desires. Respond JSON: {segment_name: string, persona_name: string, demographics: string, pain_points: string, script_hook: string, script_body: string, script_cta: string, full_script: string, tone: string, estimated_seconds: number}",
        "Segment payload: {webhook_payload}",
        [
          { field: "segment_name", as: "segment_name", type: "string" },
          { field: "persona_name", as: "persona_name", type: "string" },
          { field: "demographics", as: "persona_demographics", type: "string" },
          { field: "pain_points", as: "persona_pain_points", type: "string" },
          { field: "full_script", as: "persona_script", type: "string" },
          { field: "script_hook", as: "persona_hook", type: "string" },
          { field: "script_cta", as: "persona_cta", type: "string" },
          { field: "tone", as: "persona_tone", type: "string" },
          { field: "estimated_seconds", as: "video_seconds", type: "number" },
        ],
      ),

      // 3. Script variant creation (AI)
      aiNode(
        "3",
        "AI: Create A/B script variants",
        520,
        "You are a creative testing specialist. Create two distinct script variants (A and B) from the original persona script. Variant A should lead with emotion, Variant B should lead with logic/data. Both must maintain the persona targeting. Respond JSON: {variant_a_script: string, variant_a_hook: string, variant_b_script: string, variant_b_hook: string, variant_a_angle: string, variant_b_angle: string}",
        "Original script: {persona_script}. Persona: {persona_name}. Pain points: {persona_pain_points}. Tone: {persona_tone}.",
        [
          { field: "variant_a_script", as: "variant_a_script", type: "string" },
          { field: "variant_a_hook", as: "variant_a_hook", type: "string" },
          { field: "variant_b_script", as: "variant_b_script", type: "string" },
          { field: "variant_b_hook", as: "variant_b_hook", type: "string" },
          { field: "variant_a_angle", as: "variant_a_angle", type: "string" },
          { field: "variant_b_angle", as: "variant_b_angle", type: "string" },
        ],
      ),

      // 4. Video rendering
      n("4", "hitApi", "Render persona video ad variants", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.creatomate.com/v1/renders",
          headers: [
            { key: "Authorization", value: "Bearer {creatomate_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "template_id", value: "{video_ad_template_id}" },
            {
              key: "modifications",
              value:
                '{"hook_text":"{variant_a_hook}","script_text":"{variant_a_script}","cta_text":"{persona_cta}","duration":"{video_seconds}"}',
            },
          ],
          variables: [
            { name: "[0].id", customName: "render_id_a", type: "string" },
            { name: "[0].url", customName: "video_url_a", type: "string" },
            { name: "[0].status", customName: "render_status", type: "string" },
          ],
        },
      }),

      // 5. Creative review approval gate
      n("5", "approvalGate", "Creative review: Approve video ads", 1000, {
        approvalGateDetails: {
          approvers: ["{creative_lead_email}"],
          message:
            "Persona-targeted video ads ready for review.\n\nPersona: {persona_name}\nSegment: {segment_name}\nVariant A ({variant_a_angle}): {variant_a_hook}\nVariant B ({variant_b_angle}): {variant_b_hook}\nRender: {render_id_a}\n\nApprove to create Meta Ads campaign.",
          timeoutHours: 24,
        },
      }),

      // 6. Meta ad campaign builder
      n("6", "hitApi", "Create Meta Ads persona campaign", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v19.0/act_{ad_account_id}/campaigns",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "name",
              value: "Persona Video - {persona_name} - {segment_name}",
            },
            { key: "objective", value: "OUTCOME_ENGAGEMENT" },
            { key: "status", value: "PAUSED" },
            { key: "special_ad_categories", value: "[]" },
          ],
          variables: [
            { name: "id", customName: "persona_campaign_id", type: "string" },
          ],
        },
      }),

      // 7. Ad creative uploader
      n("7", "hitApi", "Upload video ad creatives to Meta", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v19.0/act_{ad_account_id}/adcreatives",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "name",
              value: "Variant A - {persona_name} - {variant_a_angle}",
            },
            {
              key: "object_story_spec",
              value:
                '{"page_id":"{facebook_page_id}","video_data":{"video_id":"{video_url_a}","title":"{variant_a_hook}","message":"{persona_cta}"}}',
            },
          ],
          variables: [
            { name: "id", customName: "ad_creative_id", type: "string" },
          ],
        },
      }),

      // 8. Campaign activator
      n("8", "hitApi", "Activate persona video campaign", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v19.0/{persona_campaign_id}",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "status", value: "ACTIVE" }],
          variables: [
            { name: "success", customName: "campaign_live", type: "boolean" },
          ],
        },
      }),

      // 9. Persona performance tracker (Sheets)
      sheetsNode(
        "9",
        "Track persona ad performance (Sheets)",
        1960,
        "Persona_Video_Ads",
        '[["{persona_name}","{segment_name}","{persona_demographics}","{variant_a_angle}","{variant_b_angle}","{persona_campaign_id}","{ad_creative_id}","{render_id_a}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1118. PINTEREST ADS CAMPAIGN LAUNCH (7 nodes)
  // ===================================================================

  "Pinterest Ads Campaign Launch": {
    summary:
      "Launches a Pinterest advertising campaign from a brief. AI generates keyword-rich pin descriptions, code builds the audience targeting, the campaign and ad groups are created via the Pinterest API, pin creatives are uploaded, the campaign is activated, and a launch notification is sent to Slack.",
    nodes: [
      trigger("Webhook: Pinterest campaign brief submitted", "webhook_app", {
        source: "Marketing Team / Campaign Tool",
        event: "campaign.pinterest_launch",
        howItStarts:
          "Fires when a Pinterest ad campaign brief is submitted, generating pin descriptions, building audience targeting, creating the campaign, and launching it",
        setup:
          "Configure your campaign tool to POST brief details to this webhook URL. Include campaign_name, product_name, target_keywords, budget, and creative_assets. Set up Pinterest Ads API access.",
        inputFields: [],
      }),

      // 2. AI pin description generator
      aiNode(
        "2",
        "AI: Generate Pinterest ad descriptions",
        280,
        "You are a Pinterest advertising expert. Extract the campaign details from the brief and generate 3 keyword-rich pin descriptions optimized for Pinterest search. Each description should include relevant keywords naturally. Respond JSON: {campaign_name: string, product_name: string, target_keywords: string, pin_description_1: string, pin_description_2: string, pin_description_3: string, pin_title: string, board_name: string, daily_budget: string}",
        "Campaign brief: {webhook_payload}",
        [
          { field: "campaign_name", as: "pin_campaign_name", type: "string" },
          { field: "product_name", as: "pin_product_name", type: "string" },
          {
            field: "target_keywords",
            as: "pin_target_keywords",
            type: "string",
          },
          { field: "pin_description_1", as: "pin_desc_1", type: "string" },
          { field: "pin_description_2", as: "pin_desc_2", type: "string" },
          { field: "pin_description_3", as: "pin_desc_3", type: "string" },
          { field: "pin_title", as: "pin_title", type: "string" },
          { field: "daily_budget", as: "pin_daily_budget", type: "string" },
        ],
      ),

      // 3. Audience builder (runCode)
      n("3", "runCode", "Build Pinterest audience targeting", 520, {
        runCodeDetails: {
          code: `// Build Pinterest audience targeting spec from keywords and campaign data
const keywords = (variables.pin_target_keywords || '').split(',').map(k => k.trim()).filter(Boolean);
const interestIds = keywords.slice(0, 5).map((k, i) => '847509' + String(10000 + i));
const targetingSpec = {
  KEYWORD: keywords.map(k => ({ value: k, is_negative: false })),
  INTEREST: interestIds.map(id => id),
  GENDER: ['female', 'male'],
  AGE_BUCKET: ['25-34', '35-44', '45-54'],
  LOCATION: [{ country: 'US' }]
};
return { targeting_spec: JSON.stringify(targetingSpec), keyword_count: keywords.length, audience_keywords: keywords.join(', '), targeting_summary: keywords.length + ' keywords, ages 25-54, US' };`,
          outputs: [
            { name: "targeting_spec", type: "string" },
            { name: "keyword_count", type: "number" },
            { name: "audience_keywords", type: "string" },
            { name: "targeting_summary", type: "string" },
          ],
        },
      }),

      // 4. Campaign + ad group creator
      n("4", "hitApi", "Create Pinterest campaign and ad group", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.pinterest.com/v5/campaigns",
          headers: [
            { key: "Authorization", value: "Bearer {pinterest_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "ad_account_id", value: "{pinterest_ad_account_id}" },
            { key: "name", value: "{pin_campaign_name}" },
            { key: "status", value: "PAUSED" },
            { key: "daily_spend_cap", value: "{pin_daily_budget}" },
            { key: "objective_type", value: "AWARENESS" },
          ],
          variables: [
            { name: "id", customName: "pinterest_campaign_id", type: "string" },
            {
              name: "status",
              customName: "pinterest_campaign_status",
              type: "string",
            },
          ],
        },
      }),

      // 5. Pin creative uploader
      n("5", "hitApi", "Upload pin creatives", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.pinterest.com/v5/pins",
          headers: [
            { key: "Authorization", value: "Bearer {pinterest_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "title", value: "{pin_title}" },
            { key: "description", value: "{pin_desc_1}" },
            { key: "board_id", value: "{pinterest_board_id}" },
            {
              key: "media_source",
              value: '{"source_type":"image_url","url":"{pin_creative_url}"}',
            },
            { key: "link", value: "{campaign_landing_url}" },
          ],
          variables: [
            { name: "id", customName: "pin_id", type: "string" },
            {
              name: "created_at",
              customName: "pin_created_at",
              type: "string",
            },
          ],
        },
      }),

      // 6. Campaign activation
      n("6", "hitApi", "Activate Pinterest campaign", 1240, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://api.pinterest.com/v5/campaigns/{pinterest_campaign_id}",
          headers: [
            { key: "Authorization", value: "Bearer {pinterest_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "status", value: "ACTIVE" }],
          variables: [
            {
              name: "status",
              customName: "campaign_active_status",
              type: "string",
            },
          ],
        },
      }),

      // 7. Launch notification (Slack)
      slackNode(
        "7",
        "Notify team of Pinterest launch (Slack)",
        1480,
        ":pushpin: *Pinterest Campaign Launched*\n\nCampaign: {pin_campaign_name}\nProduct: {pin_product_name}\nBudget: ${pin_daily_budget}/day\nTargeting: {targeting_summary}\nKeywords: {audience_keywords}\n\nPin: {pin_title}\nCampaign ID: {pinterest_campaign_id}\nPin ID: {pin_id}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1119. PINTEREST CONTENT CALENDAR PIN (7 nodes)
  // ===================================================================

  "Pinterest Content Calendar Pin": {
    summary:
      "Plans and schedules Pinterest content on a weekly cadence. Researches trending pin topics via SerpAPI, uses AI to batch-write pin descriptions, builds a content calendar in Sheets, routes pins through approval, publishes scheduled pins via the Pinterest API, and tracks pin performance in Sheets.",
    nodes: [
      trigger("Schedule: Weekly Pinterest content planning", "schedule", {
        schedule: "0 10 * * 1",
        howItStarts:
          "Fires every Monday at 10 AM to research trending Pinterest topics, generate pin descriptions, and schedule the week's pin content",
        setup:
          "Configure SerpAPI for Pinterest trend research. Set up Pinterest API for pin publishing. Provide Google Sheets for calendar management and performance tracking.",
        inputFields: [],
      }),

      // 2. Trending pin topic researcher (SerpAPI)
      serpApiNode(
        "2",
        "Research trending pin topics (SerpAPI)",
        280,
        "{brand_niche} Pinterest trending ideas this week",
        [
          {
            name: "organic_results",
            customName: "pin_trends_raw",
            type: "string",
          },
          {
            name: "related_searches",
            customName: "pin_related_searches",
            type: "string",
          },
          {
            name: "organic_results[0].title",
            customName: "top_pin_trend",
            type: "string",
          },
        ],
      ),

      // 3. AI pin description batch writer
      aiNode(
        "3",
        "AI: Batch-write pin descriptions for the week",
        520,
        "You are a Pinterest content expert. Based on trending topics, generate 7 pin descriptions (one per day) with optimized titles, keyword-rich descriptions, and board assignments. Each pin should target a different angle of the trending topics. Respond JSON: {pins: string, total_pins: number, primary_theme: string, weekly_hashtags: string, board_recommendations: string}",
        "Trending topics: {pin_trends_raw}. Related searches: {pin_related_searches}. Top trend: {top_pin_trend}. Brand niche: {brand_niche}.",
        [
          { field: "pins", as: "weekly_pins_json", type: "string" },
          { field: "total_pins", as: "total_weekly_pins", type: "number" },
          { field: "primary_theme", as: "pin_weekly_theme", type: "string" },
          {
            field: "weekly_hashtags",
            as: "pin_weekly_hashtags",
            type: "string",
          },
          { field: "board_recommendations", as: "board_recs", type: "string" },
        ],
      ),

      // 4. Content calendar builder (Sheets)
      sheetsNode(
        "4",
        "Build pin content calendar (Sheets)",
        760,
        "Pinterest_Calendar",
        '[["{pin_weekly_theme}","{total_weekly_pins}","{top_pin_trend}","{pin_weekly_hashtags}","{board_recs}","{weekly_pins_json}","=NOW()"]]',
      ),

      // 5. Approval routing
      n("5", "approvalGate", "Approve weekly pin schedule", 1000, {
        approvalGateDetails: {
          approvers: ["{social_media_manager_email}"],
          message:
            "Weekly Pinterest content is ready for review.\n\nTheme: {pin_weekly_theme}\nTotal pins: {total_weekly_pins}\nTop trend: {top_pin_trend}\nHashtags: {pin_weekly_hashtags}\n\nApprove to schedule all pins for the week.",
          timeoutHours: 24,
        },
      }),

      // 6. Scheduled pin publisher
      n("6", "hitApi", "Publish scheduled pins to Pinterest", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.pinterest.com/v5/pins",
          headers: [
            { key: "Authorization", value: "Bearer {pinterest_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "title", value: "{pin_weekly_theme} — Weekly Series" },
            { key: "description", value: "{weekly_pins_json}" },
            { key: "board_id", value: "{pinterest_board_id}" },
            {
              key: "media_source",
              value: '{"source_type":"image_url","url":"{pin_image_url}"}',
            },
            { key: "link", value: "{website_url}" },
          ],
          variables: [
            { name: "id", customName: "published_pin_id", type: "string" },
            {
              name: "created_at",
              customName: "pin_publish_date",
              type: "string",
            },
          ],
        },
      }),

      // 7. Pin performance tracker (Sheets)
      sheetsNode(
        "7",
        "Track pin performance (Sheets)",
        1480,
        "Pinterest_Performance",
        '[["{published_pin_id}","{pin_weekly_theme}","{total_weekly_pins}","{top_pin_trend}","{pin_weekly_hashtags}","{pin_publish_date}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1120. PINTEREST PROMOTED PIN CAMPAIGN (7 nodes)
  // ===================================================================

  "Pinterest Promoted Pin Campaign": {
    summary:
      "Creates and launches a promoted pin campaign on Pinterest. AI generates keyword-rich descriptions, code builds audience and keyword targeting, the promoted pin campaign is created via API, pin creatives are configured, the campaign is launched and validated, and performance is monitored in Sheets.",
    nodes: [
      trigger("Webhook: Promoted pin brief submitted", "webhook_app", {
        source: "Marketing Team / Campaign Tool",
        event: "campaign.promoted_pin",
        howItStarts:
          "Fires when a promoted pin brief is submitted, generating optimized descriptions, building targeting, and launching the campaign on Pinterest",
        setup:
          "Configure your campaign tool to POST promoted pin briefs to this webhook URL. Include product_name, target_keywords, budget, landing_url, and creative_image_url. Set up Pinterest Ads API access.",
        inputFields: [],
      }),

      // 2. AI keyword-rich description
      aiNode(
        "2",
        "AI: Generate keyword-rich promoted pin copy",
        280,
        "You are a Pinterest Ads copywriter. Extract the promotion details from the brief and write a keyword-optimized promoted pin description that maximizes discoverability and click-through. Include natural keyword placement and a compelling call-to-action. Respond JSON: {product_name: string, pin_title: string, pin_description: string, target_keywords: string, cta_text: string, landing_url: string, daily_budget: string, creative_url: string}",
        "Promotion brief: {webhook_payload}",
        [
          { field: "product_name", as: "promo_product_name", type: "string" },
          { field: "pin_title", as: "promo_pin_title", type: "string" },
          {
            field: "pin_description",
            as: "promo_pin_description",
            type: "string",
          },
          { field: "target_keywords", as: "promo_keywords", type: "string" },
          { field: "cta_text", as: "promo_cta", type: "string" },
          { field: "landing_url", as: "promo_landing_url", type: "string" },
          { field: "daily_budget", as: "promo_daily_budget", type: "string" },
          { field: "creative_url", as: "promo_creative_url", type: "string" },
        ],
      ),

      // 3. Audience + keyword targeting builder (runCode)
      n("3", "runCode", "Build promoted pin targeting spec", 520, {
        runCodeDetails: {
          code: `// Build targeting spec for promoted pin campaign
const keywords = (variables.promo_keywords || '').split(',').map(k => k.trim()).filter(Boolean);
const targetingSpec = {
  KEYWORD: keywords.map(k => ({ value: k, is_negative: false })),
  AGE_BUCKET: ['25-34', '35-44', '45-54'],
  GENDER: ['female', 'male'],
  LOCATION: [{ country: 'US' }, { country: 'CA' }, { country: 'GB' }],
  TARGETING_STRATEGY: 'CHOOSE_YOUR_OWN'
};
const budgetCents = Math.round(Number(variables.promo_daily_budget || '20') * 100);
return { promo_targeting_spec: JSON.stringify(targetingSpec), promo_keyword_count: keywords.length, promo_budget_micros: budgetCents * 10000, promo_targeting_summary: keywords.length + ' keywords, ages 25-54, US/CA/GB' };`,
          outputs: [
            { name: "promo_targeting_spec", type: "string" },
            { name: "promo_keyword_count", type: "number" },
            { name: "promo_budget_micros", type: "number" },
            { name: "promo_targeting_summary", type: "string" },
          ],
        },
      }),

      // 4. Promoted pin campaign creator
      n("4", "hitApi", "Create promoted pin campaign", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.pinterest.com/v5/campaigns",
          headers: [
            { key: "Authorization", value: "Bearer {pinterest_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "ad_account_id", value: "{pinterest_ad_account_id}" },
            {
              key: "name",
              value: "Promoted: {promo_product_name} - {promo_pin_title}",
            },
            { key: "status", value: "PAUSED" },
            { key: "daily_spend_cap", value: "{promo_daily_budget}" },
            { key: "objective_type", value: "WEB_CONVERSION" },
          ],
          variables: [
            { name: "id", customName: "promo_campaign_id", type: "string" },
          ],
        },
      }),

      // 5. Pin creative configurator
      n("5", "hitApi", "Configure promoted pin creative", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.pinterest.com/v5/pins",
          headers: [
            { key: "Authorization", value: "Bearer {pinterest_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "title", value: "{promo_pin_title}" },
            { key: "description", value: "{promo_pin_description}" },
            { key: "board_id", value: "{pinterest_board_id}" },
            {
              key: "media_source",
              value: '{"source_type":"image_url","url":"{promo_creative_url}"}',
            },
            { key: "link", value: "{promo_landing_url}" },
          ],
          variables: [
            { name: "id", customName: "promo_pin_id", type: "string" },
          ],
        },
      }),

      // 6. Campaign launch + validation
      n("6", "hitApi", "Launch and validate promoted campaign", 1240, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://api.pinterest.com/v5/campaigns/{promo_campaign_id}",
          headers: [
            { key: "Authorization", value: "Bearer {pinterest_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "status", value: "ACTIVE" }],
          variables: [
            {
              name: "status",
              customName: "promo_launch_status",
              type: "string",
            },
            { name: "id", customName: "validated_campaign_id", type: "string" },
          ],
        },
      }),

      // 7. Performance monitoring (Sheets)
      sheetsNode(
        "7",
        "Monitor promoted pin performance (Sheets)",
        1480,
        "Promoted_Pins",
        '[["{promo_product_name}","{promo_pin_title}","{promo_daily_budget}","{promo_keyword_count}","{promo_targeting_summary}","{promo_campaign_id}","{promo_pin_id}","{promo_launch_status}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1121. PINTEREST VIDEO PIN (8 nodes)
  // ===================================================================

  "Pinterest Video Pin": {
    summary:
      "Creates and publishes Pinterest video pins from a brief. AI writes a video script, routes through a script approval gate, triggers video rendering, generates an AI pin description, publishes the video pin, conditionally boosts with a promoted pin, and tracks performance in Sheets.",
    nodes: [
      trigger("Webhook: Video pin brief submitted", "webhook_app", {
        source: "Content Team / Brief Form",
        event: "brief.pinterest_video_pin",
        howItStarts:
          "Fires when a Pinterest video pin brief is submitted, generating a script, rendering the video, publishing the pin, and optionally boosting it as a promoted pin",
        setup:
          "Configure your content brief form to POST to this webhook URL. Include topic, target_audience, product_name, style, and duration. Set up Creatomate for video rendering and Pinterest API for pin publishing.",
        inputFields: [],
      }),

      // 2. AI video script
      aiNode(
        "2",
        "AI: Write Pinterest video script",
        280,
        "You are a Pinterest video content specialist. Extract the video brief details and write a short-form video script optimized for Pinterest's silent autoplay format. Include text overlays, visual cues, and a strong opening frame. Respond JSON: {topic: string, target_audience: string, product_name: string, video_script: string, text_overlays: string, opening_frame: string, closing_cta: string, estimated_seconds: number, style_notes: string}",
        "Video pin brief: {webhook_payload}",
        [
          { field: "topic", as: "vp_topic", type: "string" },
          { field: "target_audience", as: "vp_audience", type: "string" },
          { field: "product_name", as: "vp_product", type: "string" },
          { field: "video_script", as: "vp_script", type: "string" },
          { field: "text_overlays", as: "vp_overlays", type: "string" },
          { field: "opening_frame", as: "vp_opening", type: "string" },
          { field: "closing_cta", as: "vp_cta", type: "string" },
          { field: "estimated_seconds", as: "vp_duration", type: "number" },
        ],
      ),

      // 3. Script approval gate
      n("3", "approvalGate", "Approve video pin script", 520, {
        approvalGateDetails: {
          approvers: ["{content_manager_email}"],
          message:
            "Pinterest video pin script ready for review.\n\nTopic: {vp_topic}\nAudience: {vp_audience}\nProduct: {vp_product}\nDuration: {vp_duration}s\nOpening: {vp_opening}\nCTA: {vp_cta}\n\nFull script: {vp_script}\n\nApprove to proceed with video rendering.",
          timeoutHours: 24,
        },
      }),

      // 4. Video rendering
      n("4", "hitApi", "Render Pinterest video pin", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.creatomate.com/v1/renders",
          headers: [
            { key: "Authorization", value: "Bearer {creatomate_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "template_id", value: "{pinterest_video_template_id}" },
            {
              key: "modifications",
              value:
                '{"text_overlays":"{vp_overlays}","opening_text":"{vp_opening}","cta_text":"{vp_cta}","duration":"{vp_duration}"}',
            },
          ],
          variables: [
            { name: "[0].id", customName: "vp_render_id", type: "string" },
            { name: "[0].url", customName: "vp_video_url", type: "string" },
            {
              name: "[0].status",
              customName: "vp_render_status",
              type: "string",
            },
          ],
        },
      }),

      // 5. AI pin description
      aiNode(
        "5",
        "AI: Generate SEO-optimized pin description",
        1000,
        "You are a Pinterest SEO expert. Write a keyword-rich pin description and title for this video pin that maximizes search discoverability on Pinterest. Include relevant long-tail keywords naturally. Respond JSON: {pin_title: string, pin_description: string, pin_alt_text: string, seo_keywords: string}",
        "Topic: {vp_topic}. Product: {vp_product}. Audience: {vp_audience}. CTA: {vp_cta}.",
        [
          { field: "pin_title", as: "vp_pin_title", type: "string" },
          {
            field: "pin_description",
            as: "vp_pin_description",
            type: "string",
          },
          { field: "pin_alt_text", as: "vp_alt_text", type: "string" },
          { field: "seo_keywords", as: "vp_seo_keywords", type: "string" },
        ],
      ),

      // 6. Video pin publisher
      n("6", "hitApi", "Publish video pin to Pinterest", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.pinterest.com/v5/pins",
          headers: [
            { key: "Authorization", value: "Bearer {pinterest_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "title", value: "{vp_pin_title}" },
            { key: "description", value: "{vp_pin_description}" },
            { key: "board_id", value: "{pinterest_board_id}" },
            {
              key: "media_source",
              value:
                '{"source_type":"video_id","cover_image_url":"{vp_cover_image_url}","media_id":"{vp_video_url}"}',
            },
            { key: "link", value: "{campaign_landing_url}" },
            { key: "alt_text", value: "{vp_alt_text}" },
          ],
          variables: [
            { name: "id", customName: "vp_published_pin_id", type: "string" },
            {
              name: "created_at",
              customName: "vp_publish_time",
              type: "string",
            },
          ],
        },
      }),

      // 7. Promoted pin boost condition
      n("7", "condition", "Boost as promoted pin?", 1480, {
        conditionDetails: {
          conditions: [
            {
              variable: "{boost_as_promoted}",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),

      // 7b. Promoted boost (True path)
      n("7b", "hitApi", "Create promoted pin boost", 1660, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.pinterest.com/v5/campaigns",
          headers: [
            { key: "Authorization", value: "Bearer {pinterest_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "ad_account_id", value: "{pinterest_ad_account_id}" },
            { key: "name", value: "Boost: {vp_pin_title}" },
            { key: "status", value: "ACTIVE" },
            { key: "daily_spend_cap", value: "{boost_daily_budget}" },
            { key: "objective_type", value: "AWARENESS" },
          ],
          variables: [
            { name: "id", customName: "boost_campaign_id", type: "string" },
          ],
        },
      }),

      // 8. Performance tracker (Sheets)
      sheetsNode(
        "8",
        "Track video pin performance (Sheets)",
        1900,
        "Pinterest_Video_Pins",
        '[["{vp_topic}","{vp_product}","{vp_pin_title}","{vp_seo_keywords}","{vp_published_pin_id}","{vp_render_id}","{boost_campaign_id}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "7b", "output_1"),
      e("7", "8", "output_2"),
      e("7b", "8"),
    ],
  },

  // ===================================================================
  // 1122. PODCAST OR NEWSLETTER NURTURE (8 nodes)
  // ===================================================================

  "Podcast or Newsletter Nurture": {
    summary:
      "Nurtures new podcast or newsletter subscribers through a curated welcome sequence. Tags subscriber source via code, uses AI to curate best-of content, sends a welcome email with highlights, follows up with a deep dive after a delay, invites to community after another delay, assesses engagement via a condition, and logs the subscriber journey to Sheets.",
    nodes: [
      trigger("Webhook: New podcast/newsletter subscriber", "webhook_app", {
        source: "Podcast Platform / Newsletter Form",
        event: "subscriber.new",
        howItStarts:
          "Fires when a new subscriber joins the podcast or newsletter, initiating a curated nurture sequence that builds engagement through best-of content and community invitation",
        setup:
          "Configure your podcast platform or newsletter signup form to POST new subscriber events to this webhook URL. Include subscriber email, name, source (podcast/newsletter), and any preference data.",
        inputFields: [],
      }),

      // 2. Subscriber source tagger (runCode)
      n("2", "runCode", "Tag subscriber source and preferences", 280, {
        runCodeDetails: {
          code: `// Extract subscriber data and tag source
const payload = JSON.parse(variables.webhook_payload || '{}');
const email = payload.email || payload.subscriber_email || '';
const name = payload.name || payload.first_name || 'there';
const source = payload.source || payload.channel || 'newsletter';
const isPodcast = source.toLowerCase().includes('podcast');
const contentType = isPodcast ? 'podcast episodes' : 'newsletter articles';
const communityLink = isPodcast ? variables.podcast_community_url : variables.newsletter_community_url;
const preferences = payload.interests || payload.topics || 'general';
return { sub_email: email, sub_name: name, sub_source: source, is_podcast: isPodcast, content_type: contentType, community_link: communityLink || '{community_url}', sub_preferences: preferences };`,
          outputs: [
            { name: "sub_email", type: "string" },
            { name: "sub_name", type: "string" },
            { name: "sub_source", type: "string" },
            { name: "is_podcast", type: "boolean" },
            { name: "content_type", type: "string" },
            { name: "community_link", type: "string" },
            { name: "sub_preferences", type: "string" },
          ],
        },
      }),

      // 3. AI best-of curator
      aiNode(
        "3",
        "AI: Curate best-of content for subscriber",
        520,
        "You are a content curation specialist. Based on the subscriber's source and preferences, select the top 5 pieces of content (episodes or articles) they should read/listen to first. Craft a warm welcome message and personalized recommendations. Respond JSON: {welcome_message: string, best_of_list: string, personalized_rec: string, deep_dive_topic: string, deep_dive_content: string}",
        "Subscriber: {sub_name}. Source: {sub_source}. Content type: {content_type}. Preferences: {sub_preferences}.",
        [
          { field: "welcome_message", as: "welcome_message", type: "string" },
          { field: "best_of_list", as: "best_of_list", type: "string" },
          { field: "personalized_rec", as: "personalized_rec", type: "string" },
          { field: "deep_dive_topic", as: "deep_dive_topic", type: "string" },
          {
            field: "deep_dive_content",
            as: "deep_dive_content",
            type: "string",
          },
        ],
      ),

      // 4. Welcome email with best-of
      emailNode(
        "4",
        "Send welcome email with best-of picks",
        760,
        "{sub_email}",
        "Welcome, {sub_name}! Here are our top {content_type} for you",
        "{welcome_message}\\n\\nHere are our top picks to get you started:\\n\\n{best_of_list}\\n\\n{personalized_rec}\\n\\nWe're excited to have you!",
      ),

      // 5. Deep dive email (delay)
      n("5", "delayAction", "Wait 3 days for deep dive", 1000, {
        delayDetails: {
          delayType: "fixed",
          fixedDelay: { value: 3, unit: "days" },
        },
      }),

      // 5b. Deep dive email
      emailNode(
        "5b",
        "Send deep dive email",
        1180,
        "{sub_email}",
        "{sub_name}, dive deeper into {deep_dive_topic}",
        "Hi {sub_name},\\n\\nNow that you have had a few days to explore, we think you will love this deep dive:\\n\\n{deep_dive_content}\\n\\nThis is one of our most popular pieces on {deep_dive_topic}.\\n\\nHappy reading!",
      ),

      // 6. Community invitation email (delay)
      n("6", "delayAction", "Wait 5 days for community invite", 1360, {
        delayDetails: {
          delayType: "fixed",
          fixedDelay: { value: 5, unit: "days" },
        },
      }),

      // 6b. Community invitation
      emailNode(
        "6b",
        "Send community invitation",
        1540,
        "{sub_email}",
        "{sub_name}, join our community of {content_type} enthusiasts",
        "Hi {sub_name},\\n\\nYou have been with us for over a week now, and we hope you are enjoying our {content_type}.\\n\\nWe would love for you to join our community where members discuss ideas, share insights, and connect:\\n\\n{community_link}\\n\\nSee you inside!",
      ),

      // 7. Engagement assessment condition
      n("7", "condition", "Subscriber engaged with nurture sequence?", 1720, {
        conditionDetails: {
          conditions: [
            {
              variable: "{nurture_emails_opened}",
              operator: "greaterThan",
              value: "1",
            },
          ],
        },
      }),

      // 8. Subscriber journey logger (Sheets)
      sheetsNode(
        "8",
        "Log subscriber journey (Sheets)",
        1960,
        "Subscriber_Nurture",
        '[["{sub_email}","{sub_name}","{sub_source}","{content_type}","{sub_preferences}","{deep_dive_topic}","{nurture_emails_opened}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "5b"),
      e("5b", "6"),
      e("6", "6b"),
      e("6b", "7"),
      e("7", "8", "output_1"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1123. POINTS EARNED CONFIRMATION (7 nodes)
  // ===================================================================

  "Points Earned Confirmation": {
    summary:
      "Confirms loyalty points earned and suggests redemption options. Calculates member balance via code, uses AI to generate redemption suggestions, crafts a personalized confirmation message, sends the confirmation email, dispatches an SMS balance update via Twilio, and logs the points activity to Sheets.",
    nodes: [
      trigger("Webhook: Points earned event", "webhook_app", {
        source: "Loyalty Platform / E-commerce",
        event: "loyalty.points_earned",
        howItStarts:
          "Fires when a member earns loyalty points, confirming the transaction, updating their balance, suggesting redemption options, and notifying via email and SMS",
        setup:
          "Configure your loyalty platform to POST points earned events to this webhook URL. Include member_id, email, phone, points_earned, transaction_type, and order_id.",
        inputFields: [],
      }),

      // 2. Member balance calculator (runCode)
      n("2", "runCode", "Calculate updated member balance", 280, {
        runCodeDetails: {
          code: `// Extract points data and calculate updated balance
const payload = JSON.parse(variables.webhook_payload || '{}');
const memberEmail = payload.email || payload.member_email || '';
const memberName = payload.name || payload.member_name || 'Valued Member';
const memberPhone = payload.phone || payload.member_phone || '';
const pointsEarned = Number(payload.points_earned || payload.points || 0);
const previousBalance = Number(payload.current_balance || payload.previous_balance || 0);
const newBalance = previousBalance + pointsEarned;
const transactionType = payload.transaction_type || 'purchase';
const orderId = payload.order_id || 'N/A';
const tierThresholds = { silver: 500, gold: 2000, platinum: 5000 };
const currentTier = newBalance >= tierThresholds.platinum ? 'Platinum' : newBalance >= tierThresholds.gold ? 'Gold' : newBalance >= tierThresholds.silver ? 'Silver' : 'Bronze';
const nextTier = currentTier === 'Bronze' ? 'Silver' : currentTier === 'Silver' ? 'Gold' : currentTier === 'Gold' ? 'Platinum' : 'Max';
const pointsToNext = currentTier === 'Platinum' ? 0 : (tierThresholds[nextTier.toLowerCase()] || 0) - newBalance;
return { member_email: memberEmail, member_name: memberName, member_phone: memberPhone, points_earned: pointsEarned, previous_balance: previousBalance, new_balance: newBalance, transaction_type: transactionType, order_id: orderId, current_tier: currentTier, next_tier: nextTier, points_to_next_tier: Math.max(0, pointsToNext) };`,
          outputs: [
            { name: "member_email", type: "string" },
            { name: "member_name", type: "string" },
            { name: "member_phone", type: "string" },
            { name: "points_earned", type: "number" },
            { name: "previous_balance", type: "number" },
            { name: "new_balance", type: "number" },
            { name: "transaction_type", type: "string" },
            { name: "order_id", type: "string" },
            { name: "current_tier", type: "string" },
            { name: "next_tier", type: "string" },
            { name: "points_to_next_tier", type: "number" },
          ],
        },
      }),

      // 3. Redemption suggestion engine (AI)
      aiNode(
        "3",
        "AI: Suggest redemption options",
        520,
        "You are a loyalty program specialist. Based on the member's current points balance and tier, suggest 3 personalized redemption options they can use. Prioritize options that drive engagement and are achievable with their current balance. Respond JSON: {suggestion_1: string, suggestion_2: string, suggestion_3: string, top_recommendation: string, motivation_message: string}",
        "Member: {member_name}. Balance: {new_balance} points. Tier: {current_tier}. Earned: {points_earned} from {transaction_type}. Next tier: {next_tier} ({points_to_next_tier} points away).",
        [
          { field: "suggestion_1", as: "redemption_1", type: "string" },
          { field: "suggestion_2", as: "redemption_2", type: "string" },
          { field: "suggestion_3", as: "redemption_3", type: "string" },
          { field: "top_recommendation", as: "top_redemption", type: "string" },
          { field: "motivation_message", as: "motivation_msg", type: "string" },
        ],
      ),

      // 4. AI confirmation message
      aiNode(
        "4",
        "AI: Craft personalized confirmation",
        760,
        "You are a customer engagement specialist. Write a warm, celebratory confirmation email for a loyalty member who just earned points. Reference their tier status, new balance, and include redemption suggestions. Keep it concise and exciting. Respond JSON: {confirmation_subject: string, confirmation_body: string}",
        "Member: {member_name}. Earned: {points_earned} points from {transaction_type}. New balance: {new_balance}. Tier: {current_tier}. Suggestions: {redemption_1}, {redemption_2}, {redemption_3}. {motivation_msg}",
        [
          {
            field: "confirmation_subject",
            as: "confirm_subject",
            type: "string",
          },
          { field: "confirmation_body", as: "confirm_body", type: "string" },
        ],
      ),

      // 5. Confirmation email
      emailNode(
        "5",
        "Send points confirmation email",
        1000,
        "{member_email}",
        "{confirm_subject}",
        "{confirm_body}",
      ),

      // 6. SMS balance update (Twilio)
      smsNode(
        "6",
        "SMS: Points balance update (Twilio)",
        1240,
        "{member_phone}",
        "Hi {member_name}! You earned {points_earned} points. New balance: {new_balance} ({current_tier} tier). {top_redemption}",
      ),

      // 7. Points activity logger (Sheets)
      sheetsNode(
        "7",
        "Log points activity (Sheets)",
        1480,
        "Points_Activity",
        '[["{member_email}","{member_name}","{points_earned}","{new_balance}","{current_tier}","{transaction_type}","{order_id}","{top_redemption}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1124. POINTS-EXPIRING REMINDER (8 nodes)
  // ===================================================================

  "Points-Expiring Reminder": {
    summary:
      "Reminds loyalty members about expiring points on a daily scan. Routes by urgency level via a condition, enriches member context from HubSpot, generates an AI expiration message, sends a reminder email with a redemption guide, dispatches an urgent SMS for 7-day windows via Twilio, monitors expiration prevention, and logs events to Sheets.",
    nodes: [
      trigger("Schedule: Daily points expiration scan", "schedule", {
        schedule: "0 7 * * *",
        howItStarts:
          "Runs daily at 7 AM to scan for loyalty members with expiring points, sending tiered urgency reminders via email and SMS to prevent point loss",
        setup:
          "Configure loyalty platform webhook or API for expiring points data. Set up HubSpot for member context enrichment. Configure Twilio for urgent SMS. Provide Google Sheets for expiration event logging.",
        inputFields: [],
      }),

      // 2. Urgency level router condition
      n("2", "runCode", "Scan and classify expiring points by urgency", 280, {
        runCodeDetails: {
          code: `// Classify expiring points by urgency window
const daysUntilExpiry = Number(variables.days_until_expiry) || 30;
const memberEmail = variables.member_email || '';
const memberName = variables.member_name || 'Member';
const memberPhone = variables.member_phone || '';
const expiringPoints = Number(variables.expiring_points) || 0;
const currentBalance = Number(variables.current_balance) || 0;
const urgencyLevel = daysUntilExpiry <= 7 ? 'critical' : daysUntilExpiry <= 14 ? 'high' : daysUntilExpiry <= 30 ? 'moderate' : 'low';
const isUrgent = daysUntilExpiry <= 7;
const expiryPct = currentBalance > 0 ? ((expiringPoints / currentBalance) * 100).toFixed(1) : '0';
return { member_email: memberEmail, member_name: memberName, member_phone: memberPhone, expiring_points: expiringPoints, days_until_expiry: daysUntilExpiry, urgency_level: urgencyLevel, is_urgent: isUrgent, expiry_pct_of_balance: expiryPct, current_balance: currentBalance };`,
          outputs: [
            { name: "member_email", type: "string" },
            { name: "member_name", type: "string" },
            { name: "member_phone", type: "string" },
            { name: "expiring_points", type: "number" },
            { name: "days_until_expiry", type: "number" },
            { name: "urgency_level", type: "string" },
            { name: "is_urgent", type: "boolean" },
            { name: "expiry_pct_of_balance", type: "string" },
            { name: "current_balance", type: "number" },
          ],
        },
      }),

      // 2b. Urgency condition
      n("2b", "condition", "Points expiring within 7 days?", 520, {
        conditionDetails: {
          conditions: [
            { variable: "{is_urgent}", operator: "equals", value: "true" },
          ],
        },
      }),

      // 3. Member context enrichment (HubSpot)
      hubspotGetNode(
        "3",
        "Enrich member context from HubSpot",
        760,
        "/crm/v3/objects/contacts/{member_email}?idProperty=email&properties=firstname,lastname,hs_lead_status,total_revenue,num_associated_deals",
        [
          {
            name: "properties.firstname",
            customName: "hs_first_name",
            type: "string",
          },
          {
            name: "properties.total_revenue",
            customName: "hs_total_revenue",
            type: "string",
          },
          {
            name: "properties.num_associated_deals",
            customName: "hs_purchase_count",
            type: "string",
          },
          { name: "id", customName: "hs_contact_id", type: "string" },
        ],
      ),

      // 4. AI expiration message
      aiNode(
        "4",
        "AI: Generate expiration reminder message",
        1000,
        "You are a loyalty retention specialist. Write an expiration reminder that creates appropriate urgency without being alarmist. Tailor the tone to the urgency level. Include specific redemption suggestions based on expiring point value. Respond JSON: {reminder_subject: string, reminder_body: string, redemption_guide: string, sms_message: string, urgency_tone: string}",
        "Member: {member_name}. Expiring: {expiring_points} points in {days_until_expiry} days. Balance: {current_balance}. Urgency: {urgency_level}. Revenue: ${hs_total_revenue}. Purchases: {hs_purchase_count}.",
        [
          { field: "reminder_subject", as: "expiry_subject", type: "string" },
          { field: "reminder_body", as: "expiry_body", type: "string" },
          { field: "redemption_guide", as: "redemption_guide", type: "string" },
          { field: "sms_message", as: "expiry_sms", type: "string" },
          { field: "urgency_tone", as: "urgency_tone", type: "string" },
        ],
      ),

      // 5. Reminder email with redemption guide
      emailNode(
        "5",
        "Send expiration reminder email",
        1240,
        "{member_email}",
        "{expiry_subject}",
        "{expiry_body}\\n\\n{redemption_guide}",
      ),

      // 6. Urgent SMS for 7-day window (Twilio) — True path only
      smsNode(
        "6",
        "Urgent SMS: Points expiring soon (Twilio)",
        1480,
        "{member_phone}",
        "{expiry_sms}",
        350,
      ),

      // 7. Expiration prevention monitor (runCode)
      n("7", "runCode", "Monitor expiration prevention outcome", 1720, {
        runCodeDetails: {
          code: `// Track whether the reminder was effective
const urgency = variables.urgency_level || 'unknown';
const expiringPts = Number(variables.expiring_points) || 0;
const isUrgent = variables.is_urgent === 'true' || variables.is_urgent === true;
const outreachChannels = isUrgent ? 'email+sms' : 'email';
const estimatedSaveRate = urgency === 'critical' ? '45%' : urgency === 'high' ? '60%' : '75%';
return { outreach_channels: outreachChannels, estimated_save_rate: estimatedSaveRate, reminder_sent: true, points_at_risk: expiringPts };`,
          outputs: [
            { name: "outreach_channels", type: "string" },
            { name: "estimated_save_rate", type: "string" },
            { name: "reminder_sent", type: "boolean" },
            { name: "points_at_risk", type: "number" },
          ],
        },
      }),

      // 8. Expiration event logger (Sheets)
      sheetsNode(
        "8",
        "Log expiration event (Sheets)",
        1960,
        "Points_Expiration",
        '[["{member_email}","{member_name}","{expiring_points}","{days_until_expiry}","{urgency_level}","{outreach_channels}","{estimated_save_rate}","{current_balance}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "2b"),
      e("2b", "6", "output_1"),
      e("2b", "3", "output_2"),
      e("3", "4"),
      e("4", "5"),
      e("5", "7"),
      e("6", "3"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1125. POST-HOLIDAY REACTIVATION (8 nodes)
  // ===================================================================

  "Post-Holiday Reactivation": {
    summary:
      "Reactivates holiday shoppers in the new year. Scans for holiday purchasers on schedule, analyzes purchase history via code, uses AI to generate new-year messaging, sends a first reactivation email, follows up with a second email after a delay, nudges high-value customers via SMS through Twilio, monitors re-purchase events, and logs campaign performance to Sheets.",
    nodes: [
      trigger("Schedule: Post-holiday shopper reactivation", "schedule", {
        schedule: "0 9 2 1 *",
        howItStarts:
          "Fires on January 2nd to scan for holiday shoppers and initiate a reactivation sequence with personalized new-year messaging and multi-channel outreach",
        setup:
          "Configure access to your e-commerce platform for holiday purchase data. Set up Twilio for SMS outreach. Provide Google Sheets for campaign performance tracking.",
        inputFields: [],
      }),

      // 2. Purchase history analyzer (runCode)
      n("2", "runCode", "Analyze holiday purchase history", 280, {
        runCodeDetails: {
          code: `// Analyze holiday shoppers for reactivation targeting
const shopperEmail = variables.shopper_email || '';
const shopperName = variables.shopper_name || 'there';
const shopperPhone = variables.shopper_phone || '';
const holidaySpend = Number(variables.holiday_spend) || 0;
const orderCount = Number(variables.holiday_order_count) || 1;
const lastOrderDate = variables.last_order_date || '';
const avgOrderValue = orderCount > 0 ? (holidaySpend / orderCount).toFixed(2) : '0';
const isHighValue = holidaySpend > 200;
const topCategory = variables.top_purchased_category || 'general';
const daysSinceLastOrder = Math.floor((Date.now() - new Date(lastOrderDate).getTime()) / 86400000) || 30;
return { shopper_email: shopperEmail, shopper_name: shopperName, shopper_phone: shopperPhone, holiday_spend: holidaySpend, order_count: orderCount, avg_order_value: avgOrderValue, is_high_value: isHighValue, top_category: topCategory, days_since_last_order: daysSinceLastOrder };`,
          outputs: [
            { name: "shopper_email", type: "string" },
            { name: "shopper_name", type: "string" },
            { name: "shopper_phone", type: "string" },
            { name: "holiday_spend", type: "number" },
            { name: "order_count", type: "number" },
            { name: "avg_order_value", type: "string" },
            { name: "is_high_value", type: "boolean" },
            { name: "top_category", type: "string" },
            { name: "days_since_last_order", type: "number" },
          ],
        },
      }),

      // 3. AI new-year messaging
      aiNode(
        "3",
        "AI: Generate new-year reactivation messaging",
        520,
        "You are a retention marketing expert. Create a post-holiday reactivation email sequence for a holiday shopper. Reference their holiday purchase activity and top category. The tone should be fresh and forward-looking (new year, new goals). Respond JSON: {reactivation_subject_1: string, reactivation_body_1: string, reactivation_subject_2: string, reactivation_body_2: string, sms_nudge: string, new_year_offer: string}",
        "Shopper: {shopper_name}. Holiday spend: ${holiday_spend} across {order_count} orders. Avg order: ${avg_order_value}. Top category: {top_category}. Days since last order: {days_since_last_order}. High-value: {is_high_value}.",
        [
          {
            field: "reactivation_subject_1",
            as: "react_subject_1",
            type: "string",
          },
          { field: "reactivation_body_1", as: "react_body_1", type: "string" },
          {
            field: "reactivation_subject_2",
            as: "react_subject_2",
            type: "string",
          },
          { field: "reactivation_body_2", as: "react_body_2", type: "string" },
          { field: "sms_nudge", as: "react_sms", type: "string" },
          { field: "new_year_offer", as: "new_year_offer", type: "string" },
        ],
      ),

      // 4. Reactivation email 1
      emailNode(
        "4",
        "Send reactivation email 1",
        760,
        "{shopper_email}",
        "{react_subject_1}",
        "{react_body_1}",
      ),

      // 5. Reactivation email 2 (delay)
      n("5", "delayAction", "Wait 5 days for email 2", 1000, {
        delayDetails: {
          delayType: "fixed",
          fixedDelay: { value: 5, unit: "days" },
        },
      }),

      // 5b. Reactivation email 2
      emailNode(
        "5b",
        "Send reactivation email 2",
        1180,
        "{shopper_email}",
        "{react_subject_2}",
        "{react_body_2}",
      ),

      // 6. SMS nudge for high-value (Twilio)
      n("6", "condition", "High-value holiday shopper?", 1360, {
        conditionDetails: {
          conditions: [
            { variable: "{is_high_value}", operator: "equals", value: "true" },
          ],
        },
      }),

      // 6b. SMS for high-value — True path
      smsNode(
        "6b",
        "SMS: New year nudge for VIP shoppers (Twilio)",
        1540,
        "{shopper_phone}",
        "{react_sms}",
        350,
      ),

      // 7. Re-purchase tracker (runCode)
      n("7", "runCode", "Track reactivation outcome", 1720, {
        runCodeDetails: {
          code: `// Track reactivation campaign outcome
const isHighValue = variables.is_high_value === 'true' || variables.is_high_value === true;
const channelsUsed = isHighValue ? 'email_x2+sms' : 'email_x2';
const offer = variables.new_year_offer || 'none';
return { reactivation_channels: channelsUsed, offer_sent: offer, reactivation_status: 'sequence_completed' };`,
          outputs: [
            { name: "reactivation_channels", type: "string" },
            { name: "offer_sent", type: "string" },
            { name: "reactivation_status", type: "string" },
          ],
        },
      }),

      // 8. Campaign performance logger (Sheets)
      sheetsNode(
        "8",
        "Log reactivation campaign (Sheets)",
        1960,
        "Post_Holiday_Reactivation",
        '[["{shopper_email}","{shopper_name}","{holiday_spend}","{order_count}","{top_category}","{is_high_value}","{reactivation_channels}","{offer_sent}","{reactivation_status}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "5b"),
      e("5b", "6"),
      e("6", "6b", "output_1"),
      e("6", "7", "output_2"),
      e("6b", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1126. POST-PURCHASE LOYALTY BRANCH BY ORDER COUNT (8 nodes)
  // ===================================================================

  "Post-Purchase Loyalty Branch by Order Count": {
    summary:
      "Routes post-purchase communications based on customer order count. Looks up order history from Shopify, routes via an order count condition, sends a first-order welcome-to-loyalty email, a repeat buyer reward email, or a loyal customer VIP preview email, delivers VIP concierge treatment for top-tier customers, and logs loyalty events to Sheets.",
    nodes: [
      trigger("Webhook: Order completed", "webhook_app", {
        source: "Shopify / E-commerce Platform",
        event: "orders/paid",
        howItStarts:
          "Fires when an order is completed, looking up the customer's order history and routing them into the appropriate loyalty tier communication path",
        setup:
          "Configure Shopify to POST order completion webhooks to this URL. Include customer_id, email, and order details. Set up email templates for each loyalty tier.",
        inputFields: [],
      }),

      // 2. Order count lookup (Shopify)
      n("2", "hitApi", "Look up customer order history (Shopify)", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/customers/search.json?query=email:{customer_email}",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "customers[0].email",
              customName: "customer_email",
              type: "string",
            },
            {
              name: "customers[0].first_name",
              customName: "customer_name",
              type: "string",
            },
            {
              name: "customers[0].orders_count",
              customName: "total_order_count",
              type: "number",
            },
            {
              name: "customers[0].total_spent",
              customName: "customer_total_spent",
              type: "string",
            },
            {
              name: "customers[0].id",
              customName: "shopify_customer_id",
              type: "string",
            },
          ],
        },
      }),

      // 2b. Extract webhook data
      n("2b", "runCode", "Classify customer loyalty tier", 460, {
        runCodeDetails: {
          code: `// Classify customer into loyalty tier based on order count
const orderCount = Number(variables.total_order_count) || 1;
const totalSpent = Number(variables.customer_total_spent) || 0;
const isFirstOrder = orderCount === 1;
const isRepeatBuyer = orderCount >= 2 && orderCount <= 5;
const isLoyalCustomer = orderCount >= 6 && orderCount <= 15;
const isVIP = orderCount > 15;
const tier = isVIP ? 'vip' : isLoyalCustomer ? 'loyal' : isRepeatBuyer ? 'repeat' : 'first_order';
return { is_first_order: isFirstOrder, is_repeat_buyer: isRepeatBuyer, is_loyal_customer: isLoyalCustomer, is_vip: isVIP, loyalty_tier: tier, order_count: orderCount, lifetime_value: totalSpent };`,
          outputs: [
            { name: "is_first_order", type: "boolean" },
            { name: "is_repeat_buyer", type: "boolean" },
            { name: "is_loyal_customer", type: "boolean" },
            { name: "is_vip", type: "boolean" },
            { name: "loyalty_tier", type: "string" },
            { name: "order_count", type: "number" },
            { name: "lifetime_value", type: "number" },
          ],
        },
      }),

      // 3. Order count router condition
      n("3", "condition", "First-time buyer?", 640, {
        conditionDetails: {
          conditions: [
            { variable: "{is_first_order}", operator: "equals", value: "true" },
          ],
        },
      }),

      // 4. First-order welcome to loyalty email (True path)
      emailNode(
        "4",
        "Welcome to our loyalty program!",
        880,
        "{customer_email}",
        "Welcome to the family, {customer_name}! Your loyalty journey starts now",
        "Hi {customer_name},\\n\\nThank you for your first order! We are thrilled to welcome you.\\n\\nYou have automatically been enrolled in our loyalty program. Here is how it works:\\n\\n- Earn points on every purchase\\n- Unlock exclusive rewards as you level up\\n- Get early access to sales and new products\\n\\nYour current balance: 100 welcome points!\\n\\nStart earning more today.",
      ),

      // 5. Repeat buyer reward email (False path — goes to second condition)
      n(
        "5",
        "condition",
        "VIP customer (15+ orders)?",
        880,
        {
          conditionDetails: {
            conditions: [
              { variable: "{is_vip}", operator: "equals", value: "true" },
            ],
          },
        },
        350,
      ),

      // 5b. Repeat / Loyal buyer email (False path of VIP condition)
      emailNode(
        "5b",
        "Reward email for repeat/loyal customers",
        1120,
        "{customer_email}",
        "{customer_name}, order #{order_count} earned you bonus rewards!",
        "Hi {customer_name},\\n\\nOrder #{order_count} is confirmed! As a {loyalty_tier} member, you have earned bonus points.\\n\\nLifetime value: ${lifetime_value}\\nTier: {loyalty_tier}\\n\\nKeep shopping to unlock even more rewards and exclusive perks!",
        350,
      ),

      // 6. Loyal customer VIP preview email (True path of VIP condition)
      emailNode(
        "6",
        "VIP preview + concierge welcome",
        1120,
        "{customer_email}",
        "{customer_name}, welcome to VIP — exclusive previews inside",
        "Dear {customer_name},\\n\\nWith {order_count} orders and ${lifetime_value} in lifetime value, you are one of our most valued customers.\\n\\nAs a VIP member, you now have access to:\\n- Exclusive product previews before anyone else\\n- Priority customer support (dedicated concierge)\\n- Special VIP-only pricing and bundles\\n- Early access to every sale\\n\\nYour dedicated concierge team will be in touch shortly.\\n\\nThank you for your incredible loyalty.",
      ),

      // 7. VIP concierge treatment (Slack alert for concierge team)
      slackNode(
        "7",
        "Alert concierge team for VIP (Slack)",
        1360,
        ":star2: *New VIP Customer Alert*\n\nCustomer: {customer_name} ({customer_email})\nOrders: {order_count}\nLifetime Value: ${lifetime_value}\nShopify ID: {shopify_customer_id}\n\nPlease initiate concierge welcome outreach within 24 hours.",
      ),

      // 8. Loyalty event logger (Sheets)
      sheetsNode(
        "8",
        "Log loyalty tier event (Sheets)",
        1600,
        "Loyalty_Events",
        '[["{customer_email}","{customer_name}","{order_count}","{lifetime_value}","{loyalty_tier}","{is_vip}","{shopify_customer_id}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "2b"),
      e("2b", "3"),
      e("3", "4", "output_1"),
      e("3", "5", "output_2"),
      e("4", "8"),
      e("5", "6", "output_1"),
      e("5", "5b", "output_2"),
      e("5b", "8"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1127. PREDICTIVE CHURN SCORE CALCULATOR (9 nodes)
  // ===================================================================

  "Predictive Churn Score Calculator": {
    summary:
      "Calculates predictive churn scores from multi-signal analysis on a daily schedule. Collects behavioral signals, transaction signals, and engagement signals, computes a composite churn score via code, classifies risk tiers, updates the CRM risk profile in HubSpot, alerts the team via Slack for high-risk accounts, and triggers intervention workflows.",
    nodes: [
      trigger("Schedule: Daily churn prediction pipeline", "schedule", {
        schedule: "0 6 * * *",
        howItStarts:
          "Runs daily at 6 AM to collect behavioral, transaction, and engagement signals, compute predictive churn scores, classify risk tiers, and trigger interventions for at-risk customers",
        setup:
          "Configure API access to your application analytics, billing system, and engagement tracking. Set up HubSpot for CRM risk updates. Provide Slack webhook for high-risk alerts.",
        inputFields: [],
      }),

      // 2. Behavioral signal collector
      n("2", "hitApi", "Collect behavioral signals", 280, {
        hitApiDetails: {
          method: "GET",
          url: "{analytics_api_url}/users/{customer_id}/behavior?period=30d",
          headers: [
            { key: "Authorization", value: "Bearer {analytics_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "login_count",
              customName: "login_count_30d",
              type: "number",
            },
            {
              name: "feature_usage_score",
              customName: "feature_usage_score",
              type: "number",
            },
            {
              name: "session_duration_avg",
              customName: "avg_session_minutes",
              type: "number",
            },
            {
              name: "last_active_date",
              customName: "last_active_date",
              type: "string",
            },
            {
              name: "support_tickets",
              customName: "support_tickets_30d",
              type: "number",
            },
            { name: "email", customName: "customer_email", type: "string" },
            { name: "name", customName: "customer_name", type: "string" },
            { name: "company", customName: "customer_company", type: "string" },
          ],
        },
      }),

      // 3. Transaction signal collector
      n("3", "hitApi", "Collect transaction signals", 520, {
        hitApiDetails: {
          method: "GET",
          url: "{billing_api_url}/customers/{customer_id}/metrics?period=90d",
          headers: [
            { key: "Authorization", value: "Bearer {billing_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "mrr", customName: "current_mrr", type: "number" },
            { name: "mrr_trend", customName: "mrr_trend", type: "string" },
            {
              name: "payment_failures",
              customName: "payment_failures_90d",
              type: "number",
            },
            {
              name: "plan_downgrades",
              customName: "plan_downgrades",
              type: "number",
            },
            {
              name: "contract_end_date",
              customName: "contract_end_date",
              type: "string",
            },
            {
              name: "days_to_renewal",
              customName: "days_to_renewal",
              type: "number",
            },
          ],
        },
      }),

      // 4. Engagement signal collector
      n("4", "hitApi", "Collect engagement signals", 760, {
        hitApiDetails: {
          method: "GET",
          url: "{engagement_api_url}/contacts/{customer_email}/engagement?period=30d",
          headers: [
            { key: "Authorization", value: "Bearer {engagement_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "email_open_rate",
              customName: "email_open_rate_30d",
              type: "number",
            },
            {
              name: "email_click_rate",
              customName: "email_click_rate_30d",
              type: "number",
            },
            {
              name: "nps_score",
              customName: "latest_nps_score",
              type: "number",
            },
            {
              name: "community_posts",
              customName: "community_activity",
              type: "number",
            },
            {
              name: "webinar_attendance",
              customName: "webinar_attendance",
              type: "number",
            },
          ],
        },
      }),

      // 5. Churn score calculator (runCode)
      n("5", "runCode", "Calculate composite churn risk score", 1000, {
        runCodeDetails: {
          code: `// Multi-signal churn risk scoring algorithm
const loginCount = Number(variables.login_count_30d) || 0;
const featureUsage = Number(variables.feature_usage_score) || 0;
const sessionMins = Number(variables.avg_session_minutes) || 0;
const supportTickets = Number(variables.support_tickets_30d) || 0;
const paymentFailures = Number(variables.payment_failures_90d) || 0;
const planDowngrades = Number(variables.plan_downgrades) || 0;
const mrrTrend = variables.mrr_trend || 'flat';
const daysToRenewal = Number(variables.days_to_renewal) || 365;
const emailOpenRate = Number(variables.email_open_rate_30d) || 0;
const npsScore = Number(variables.latest_nps_score) || 5;
const communityActivity = Number(variables.community_activity) || 0;

// Behavioral score (0-30)
const behaviorScore = Math.min(30, Math.max(0,
  (loginCount < 3 ? 15 : loginCount < 7 ? 8 : 0) +
  (featureUsage < 20 ? 10 : featureUsage < 50 ? 5 : 0) +
  (sessionMins < 2 ? 5 : 0)
));

// Transaction score (0-35)
const transactionScore = Math.min(35, Math.max(0,
  (paymentFailures * 8) +
  (planDowngrades * 12) +
  (mrrTrend === 'declining' ? 10 : mrrTrend === 'flat' ? 3 : 0) +
  (daysToRenewal < 30 ? 5 : 0)
));

// Engagement score (0-35)
const engagementScore = Math.min(35, Math.max(0,
  (emailOpenRate < 10 ? 15 : emailOpenRate < 25 ? 8 : 0) +
  (npsScore <= 3 ? 12 : npsScore <= 5 ? 6 : 0) +
  (communityActivity === 0 ? 5 : 0) +
  (supportTickets > 3 ? 3 : 0)
));

const churnScore = behaviorScore + transactionScore + engagementScore;
const riskTier = churnScore >= 70 ? 'critical' : churnScore >= 50 ? 'high' : churnScore >= 30 ? 'moderate' : 'low';
const isHighRisk = churnScore >= 50;

return { churn_score: churnScore, behavior_score: behaviorScore, transaction_score: transactionScore, engagement_score: engagementScore, risk_tier: riskTier, is_high_risk: isHighRisk, scoring_breakdown: 'Behavior: ' + behaviorScore + '/30, Transaction: ' + transactionScore + '/35, Engagement: ' + engagementScore + '/35' };`,
          outputs: [
            { name: "churn_score", type: "number" },
            { name: "behavior_score", type: "number" },
            { name: "transaction_score", type: "number" },
            { name: "engagement_score", type: "number" },
            { name: "risk_tier", type: "string" },
            { name: "is_high_risk", type: "boolean" },
            { name: "scoring_breakdown", type: "string" },
          ],
        },
      }),

      // 6. Risk tier classifier (condition)
      n("6", "condition", "Customer is high-risk (score >= 50)?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "{is_high_risk}", operator: "equals", value: "true" },
          ],
        },
      }),

      // 7. CRM risk update (HubSpot)
      hubspotPostNode(
        "7",
        "Update churn risk in HubSpot CRM",
        1480,
        "/crm/v3/objects/contacts/{customer_email}?idProperty=email",
        [
          {
            key: "properties",
            value:
              '{"churn_risk_score":"{churn_score}","churn_risk_tier":"{risk_tier}","churn_score_breakdown":"{scoring_breakdown}","churn_scored_date":"{today_date}"}',
          },
        ],
        [{ name: "id", customName: "updated_contact_id", type: "string" }],
      ),

      // 8. High-risk Slack alerting (True path)
      slackNode(
        "8",
        "Alert: High churn risk customer (Slack)",
        1720,
        ":rotating_light: *High Churn Risk Alert*\n\nCustomer: {customer_name} ({customer_email})\nCompany: {customer_company}\nChurn Score: {churn_score}/100 ({risk_tier})\n\nBreakdown: {scoring_breakdown}\n\nMRR: ${current_mrr} | Trend: {mrr_trend}\nDays to renewal: {days_to_renewal}\nNPS: {latest_nps_score} | Logins (30d): {login_count_30d}\n\nImmediate intervention recommended.",
        350,
      ),

      // 9. Intervention trigger (sends to intervention workflow)
      n("9", "hitApi", "Trigger churn intervention workflow", 1960, {
        hitApiDetails: {
          method: "POST",
          url: "{intervention_workflow_webhook_url}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "customer_email", value: "{customer_email}" },
            { key: "customer_name", value: "{customer_name}" },
            { key: "customer_company", value: "{customer_company}" },
            { key: "churn_score", value: "{churn_score}" },
            { key: "risk_tier", value: "{risk_tier}" },
            { key: "current_mrr", value: "{current_mrr}" },
            { key: "scoring_breakdown", value: "{scoring_breakdown}" },
            { key: "days_to_renewal", value: "{days_to_renewal}" },
          ],
          variables: [
            {
              name: "status",
              customName: "intervention_triggered",
              type: "string",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "8", "output_1"),
      e("6", "7", "output_2"),
      e("7", "9"),
      e("8", "7"),
    ],
  },
};
